import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { libraryApi } from '../../lib/api/libraryApi';
import AssessmentCreateFlowModal from '../assessments/AssessmentCreateFlowModal';
import AIInterviewViewModal from 'src/components/library/AIInterviewViewModal';
import CandidateInsightModal from '../../components/results/CandidateInsightModal';
import ExtractionFallbacksModal from './ExtractionFallbacksModal';
import ApplyLinkFieldsAdmin from './ApplyLinkFieldsAdmin';
import ScreeningRulesAdmin from './ScreeningRulesAdmin';
import TableLayoutAdmin from './TableLayoutAdmin';
import QualifyingAgentAdmin from './QualifyingAgentAdmin';
import CandidateAssessmentScoreContent from '../../components/results/CandidateAssessmentScoreContent';
import CandidateProctoringDetailsContent from '../../components/results/CandidateProctoringDetailsContent';
import InterviewSessionModal from '../results/components/InterviewSessionModal';
import { getCandidatesTableLayout } from '../screenings/candidatesTableLayout';
import { useAuth } from '../../contexts/AuthContext';
import { motion, AnimatePresence } from 'motion/react';
import {
  Building2,
  Users,
  Briefcase,
  LifeBuoy,
  Sparkles,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  FileCheck,
  Video,
  TrendingUp,
  Upload,
  CreditCard,
  Clock,
  AlertTriangle,
  DollarSign,
  Layers,
  Edit3,
  Save,
  Columns,
  X,
  ExternalLink,
  ArrowLeft,
  Star,
  FileText,
  Link2,
  Mic,
  Eye,
  Check,
  RotateCcw,
  Info,
  Search,
  ScanLine,
  MoreVertical,
  Trash2,
  Shield,
  PhoneCall as PhoneCallIcon,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { getCombinedScreeningProctoringBadge } from '../../components/results/proctoringUtils';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '../../components/ui/ui/tooltip';

/* ─── helpers ─────────────────────────────────────────────────────────── */

const fmtINR = (v) =>
  Number(v || 0).toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  });

/* Resume click handler for presigned URLs */
const handleResumeClick = async (candidateId, e) => {
  e.preventDefault();
  try {
    const { data } = await axios.get(`/api/screenings/candidates/${candidateId}/resume-view-url`);
    if (data?.url) {
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } else {
      toast.error('Resume not available');
    }
  } catch (error) {
    toast.error(error?.response?.data?.message || 'Failed to open resume');
  }
};

/** INR with up to 2 decimals (for small estimated costs). */
const fmtINRDec = (v) =>
  Number(v || 0).toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const fmtUSD = (v) =>
  Number(v || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const capitalize = (s) => {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const daysBetween = (a, b) => {
  const msPerDay = 86_400_000;
  return Math.ceil((new Date(b) - new Date(a)) / msPerDay);
};

/** Credit usage percentage formatted to 3 decimal places. */
const fmtPct3 = (pct) =>
  typeof pct === 'number' && !Number.isNaN(pct)
    ? (Math.round(pct * 1000) / 1000).toFixed(3)
    : '0.000';

const USAGE_RANGE_OPTIONS = [
  { key: '30d', label: 'Last 30 days' },
  { key: '90d', label: 'Last 90 days' },
  { key: 'all', label: 'All time' },
  { key: 'custom', label: 'Custom' },
];

/** Plan quota defaults for display when API returns null (unmigrated orgs) */
const QUOTA_DEFAULTS = {
  starter: { video_interviews: 25, audio_interviews: 25, assessments: 50, resume_scores: 300 },
  core: { video_interviews: 75, audio_interviews: 75, assessments: 200, resume_scores: 1000 },
  growth: { video_interviews: 75, audio_interviews: 75, assessments: 200, resume_scores: 1000 },
};

const buildUsageRangeParams = (usageRange) => {
  if (!usageRange || usageRange.preset === 'all') return {};
  if (usageRange.preset === 'custom') {
    if (usageRange.from && usageRange.to) {
      return { from: usageRange.from, to: usageRange.to };
    }
    return {};
  }
  const days = usageRange.preset === '90d' ? 90 : 30;
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - (days - 1));
  return {
    from: from.toISOString(),
    to: to.toISOString(),
  };
};

const planColor = (plan) => {
  const p = (plan || '').toLowerCase();
  if (p === 'starter') return 'bg-emerald-100 text-emerald-700';
  if (p === 'core') return 'bg-blue-100 text-blue-700';
  if (p === 'custom') return 'bg-purple-100 text-purple-700';
  if (p === 'free') return 'bg-gray-100 text-gray-500';
  return 'bg-gray-100 text-gray-600';
};

const statusColor = (status) =>
  (status || '').toLowerCase() === 'active'
    ? 'bg-emerald-100 text-emerald-700'
    : 'bg-gray-100 text-gray-500';

const priorityColor = (p) => {
  const s = (p || '').toLowerCase();
  if (s === 'critical') return 'bg-red-100 text-red-700';
  if (s === 'high') return 'bg-orange-100 text-orange-700';
  if (s === 'medium') return 'bg-yellow-100 text-yellow-700';
  return 'bg-gray-100 text-gray-600';
};

const supportStatusColor = (s) => {
  const v = (s || '').toLowerCase();
  if (v === 'open') return 'bg-blue-100 text-blue-700';
  if (v === 'in_progress' || v === 'in-progress') return 'bg-amber-100 text-amber-700';
  if (v === 'resolved' || v === 'closed') return 'bg-emerald-100 text-emerald-700';
  return 'bg-gray-100 text-gray-600';
};

/* ─── small reusable pieces ───────────────────────────────────────────── */

function StatCard({ icon: Icon, iconBg, label, value, subtitle, onClick }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
      className={cn(
        'rounded-2xl border border-gray-200 bg-white p-5 shadow-sm flex items-start gap-4',
        onClick && 'cursor-pointer hover:shadow-md hover:border-[#007BFF]/30 transition-all text-left'
      )}
    >
      <div className={cn('flex h-11 w-11 shrink-0 items-center justify-center rounded-full', iconBg)}>
        <Icon className="h-5 w-5 text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-sm text-gray-500 font-medium">{label}</p>
        <p className="text-2xl font-semibold text-gray-900 tabular-nums leading-tight mt-0.5">
          {value ?? '—'}
        </p>
        {subtitle && (
          <p className="text-xs text-gray-400 mt-1 truncate">{subtitle}</p>
        )}
      </div>
    </motion.div>
  );
}

function ProgressBar({ pct, colorClass }) {
  const clamped = Math.min(100, Math.max(0, pct || 0));
  return (
    <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
      <div
        className={cn('h-full rounded-full transition-all duration-500', colorClass)}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

function Badge({ className, children }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium leading-none',
        className,
      )}
    >
      {children}
    </span>
  );
}

function Spinner({ className }) {
  return (
    <svg
      className={cn('animate-spin h-5 w-5 text-blue-500', className)}
      viewBox="0 0 24 24"
      fill="none"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

function EmptyState({ text }) {
  return (
    <div className="flex items-center justify-center rounded-xl border-2 border-dashed border-gray-200 py-12 text-sm text-gray-400">
      {text}
    </div>
  );
}

function DpaStatusPill({ acceptedAt }) {
  const approved = !!acceptedAt;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-[11px] font-semibold border',
        approved
          ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
          : 'bg-gray-50 border-gray-200 text-gray-500'
      )}
      title={approved ? `Approved on ${fmtDate(acceptedAt)}` : 'Not submitted'}
    >
      <span className={cn('h-2 w-2 rounded-full', approved ? 'bg-emerald-500' : 'bg-gray-400')} />
      {approved ? 'Approved' : '—'}
    </span>
  );
}

function DpaManagementTab({ orgId, orgName, orgEmail }) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`/api/internal/org/${orgId}/dpa-status`);
      setStatus(data);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to load DPA status');
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const downloadTemplate = async () => {
    try {
      const { data } = await axios.get('/api/internal/dpa-template-url');
      if (data?.url) window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to get DPA template');
    }
  };

  const viewSigned = async () => {
    try {
      const rawUrl = status?.dpaUploadedFileUrl;
      if (!rawUrl) return;
      const { data } = await axios.get('/api/internal/dpa-view-url', { params: { url: rawUrl } });
      if (data?.url) window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to open signed DPA');
    }
  };

  const approve = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const uploadRes = await axios.post(`/api/internal/org/${orgId}/dpa-upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const fileUrl = uploadRes.data?.fileUrl;
      if (!fileUrl) throw new Error('Missing uploaded file URL');
      await axios.patch(`/api/internal/org/${orgId}/dpa-approve`, { dpaUploadedFileUrl: fileUrl });
      toast.success(
        'DPA approved. Document collection is now enabled for this organization.',
        { duration: 5000 }
      );
      setFile(null);
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to approve DPA');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    const ok = window.confirm(
      'Are you sure? This will disable document collection for this organization.'
    );
    if (!ok) return;
    setBusy(true);
    try {
      await axios.patch(`/api/internal/org/${orgId}/dpa-revoke`);
      toast.success('DPA revoked');
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to revoke DPA');
    } finally {
      setBusy(false);
    }
  };

  const approved = status?.dpaStatus === 'approved';

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-gray-900">DPA Management</div>
            <div className="text-xs text-gray-500 mt-1">
              Organisation: <span className="font-medium text-gray-700">{orgName}</span>
              {orgEmail ? (
                <>
                  {' '}
                  · Contact: <span className="font-medium text-gray-700">{orgEmail}</span>
                </>
              ) : null}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DpaStatusPill acceptedAt={status?.dpaAcceptedAt} />
          </div>
        </div>

        {loading ? (
          <div className="mt-5 flex items-center gap-3 text-sm text-gray-600">
            <Spinner className="h-5 w-5" />
            Loading DPA status…
          </div>
        ) : approved ? (
          <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <div className="text-sm font-semibold text-emerald-900">✅ DPA Approved</div>
            <div className="mt-2 text-sm text-emerald-900/80">
              Approved on: <span className="font-medium">{fmtDate(status?.dpaAcceptedAt)}</span>
            </div>
            <div className="mt-1 text-sm text-emerald-900/80">
              Approved by:{' '}
              <span className="font-medium">
                {status?.dpaApprovedBy?.name || status?.dpaApprovedBy?.email || '—'}
              </span>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={viewSigned}
                className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 transition-colors"
                disabled={busy || !status?.dpaUploadedFileUrl}
              >
                <ExternalLink className="h-4 w-4" />
                View Signed DPA
              </button>
              <button
                type="button"
                onClick={revoke}
                className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors"
                disabled={busy}
              >
                <X className="h-4 w-4" />
                Revoke DPA
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="text-sm font-semibold text-amber-900">Pending</div>
            <div className="mt-2 text-sm text-amber-900/80">
              This organisation has not yet submitted a signed Data Processing Agreement.
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={downloadTemplate}
                className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-white px-4 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-100 transition-colors"
              >
                <FileText className="h-4 w-4" />
                Download DPA Template
              </button>
            </div>

            <div className="mt-4 rounded-lg border border-amber-200 bg-white p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                Upload signed DPA (PDF, max 20 MB)
              </div>
              <input
                type="file"
                accept=".pdf,application/pdf"
                className="mt-3 block w-full text-sm"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  disabled={!file || busy}
                  onClick={approve}
                  className={cn(
                    'inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors',
                    !file || busy
                      ? 'bg-[#007BFF]/50 text-white cursor-not-allowed opacity-60'
                      : 'bg-[#007BFF] text-white hover:bg-[#0a66d6]'
                  )}
                >
                  {busy ? <Spinner className="h-4 w-4" /> : <FileCheck className="h-4 w-4" />}
                  Approve DPA
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TabPills({ tabs, active, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {tabs.map((t) => (
        <button
          key={t.key}
          data-testid={`org-tab-${t.key}`}
          onClick={() => onChange(t.key)}
          className={cn(
            'rounded-lg px-4 py-1.5 text-sm font-medium transition-colors',
            active === t.key
              ? 'bg-[#007BFF] text-white shadow-sm'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/* ─── expanded org detail tabs ────────────────────────────────────────── */

/** Plan quota defaults for display when subscription has no V1 quotas yet */
const PLAN_QUOTA_DEFAULTS = {
  starter: { video_interviews: 25, audio_interviews: 25, assessments: 50, resume_scores: 300 },
  core: { video_interviews: 75, audio_interviews: 75, assessments: 200, resume_scores: 1000 },
  growth: { video_interviews: 75, audio_interviews: 75, assessments: 200, resume_scores: 1000 },
  enterprise: null,
};

function OverviewTab({ detail, usageRange, onUsageRangeChange }) {
  const quotas = detail.quotas || detail.quotaUsage || null;
  const showRangeControls = typeof onUsageRangeChange === 'function';
  const [voiceAccordionOpen, setVoiceAccordionOpen] = useState(true);
  const [videoAccordionOpen, setVideoAccordionOpen] = useState(true);
  const ocrCallsCount = Number(detail?.ocrCallsTotal || 0);
  const ocrCallsDisplay = ocrCallsCount.toLocaleString('en-IN');

  return (
    <>
      {showRangeControls && (
        <div className="flex justify-end mb-3">
          <div className="inline-flex items-center gap-2 text-xs">
            <span className="text-gray-500">Usage range:</span>
            <div className="inline-flex rounded-full bg-gray-100 p-0.5">
              {USAGE_RANGE_OPTIONS.map((opt) => {
                const isActive =
                  usageRange?.preset === opt.key || (!usageRange && opt.key === '30d');
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() =>
                      onUsageRangeChange(
                        opt.key === 'custom'
                          ? {
                            preset: 'custom',
                            from: usageRange?.from || '',
                            to: usageRange?.to || '',
                          }
                          : { preset: opt.key, from: null, to: null },
                      )
                    }
                    className={cn(
                      'px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors',
                      isActive
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900',
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {usageRange?.preset === 'custom' && (
              <div className="flex items-center gap-1">
                <input
                  type="date"
                  value={usageRange.from || ''}
                  onChange={(e) =>
                    onUsageRangeChange({
                      ...usageRange,
                      preset: 'custom',
                      from: e.target.value || '',
                    })
                  }
                  className="border border-gray-200 rounded-md px-1.5 py-0.5 text-[11px] text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <span className="text-gray-400">–</span>
                <input
                  type="date"
                  value={usageRange.to || ''}
                  onChange={(e) =>
                    onUsageRangeChange({
                      ...usageRange,
                      preset: 'custom',
                      to: e.target.value || '',
                    })
                  }
                  className="border border-gray-200 rounded-md px-1.5 py-0.5 text-[11px] text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Plan Usage — V2 credits or V1 per-feature quotas */}
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <div className="flex items-center gap-2 mb-5">
            <Video className="h-4 w-4 text-violet-500" />
            <h4 className="text-sm font-semibold text-gray-800">Plan Usage</h4>
          </div>
          {quotas?.version === 'v2' ? (
            <div className="space-y-3.5 text-sm">
              {/* Credit bar */}
              {(() => {
                const total = quotas.credits ?? 0;
                const used = quotas.creditsUsed ?? 0;
                const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
                return (
                  <div className="py-0.5">
                    <div className="flex justify-between items-center mb-0.5">
                      <span className="text-gray-500 flex items-center gap-1.5 font-medium">Credits</span>
                      <span className="font-semibold tabular-nums text-gray-800">{used} / {total}</span>
                    </div>
                    <ProgressBar pct={pct} colorClass={pct >= 90 ? 'bg-red-500' : 'bg-[#007BFF]'} />
                  </div>
                );
              })()}
              {/* Breakdown */}
              <div className="pl-1 space-y-1.5 text-xs text-gray-500">
                <div className="flex justify-between"><span className="flex items-center gap-1.5"><Video className="h-3 w-3" /> Interviews</span><span className="tabular-nums text-gray-700">{quotas.breakdown?.interviews?.count ?? 0} <span className="text-gray-400">(× 1 cr)</span></span></div>
                <div className="flex justify-between"><span className="flex items-center gap-1.5"><FileCheck className="h-3 w-3" /> Assessments</span><span className="tabular-nums text-gray-700">{quotas.breakdown?.assessments?.count ?? 0} <span className="text-gray-400">(× 0.5 cr)</span></span></div>
                <div className="flex justify-between"><span className="flex items-center gap-1.5"><FileText className="h-3 w-3" /> Resume Scores</span><span className="tabular-nums text-gray-700">{quotas.resumeScores ?? 0} <span className="text-gray-400">(unlimited)</span></span></div>
                <div className="flex justify-between"><span className="flex items-center gap-1.5"><ScanLine className="h-3 w-3" /> OCR Calls</span><span className="tabular-nums text-gray-700">{ocrCallsDisplay}</span></div>
              </div>
              {quotas.teamUsers?.quota != null && (
                <div className="flex justify-between items-center pt-2 border-t border-gray-50 text-xs">
                  <span className="text-gray-500">Team Users</span>
                  <span className="font-medium tabular-nums text-gray-700">{quotas.teamUsers.used ?? 0} / {quotas.teamUsers.quota}</span>
                </div>
              )}
              {/* V2 On-demand usage */}
              {(() => {
                const odInterview = quotas.onDemand?.interview || { count: 0, totalSpend: 0 };
                const odAssessment = quotas.onDemand?.assessment || { count: 0, totalSpend: 0 };
                const totalOdCount = odInterview.count + odAssessment.count;
                const totalOdSpend = quotas.onDemand?.totalSpend ?? (odInterview.totalSpend + odAssessment.totalSpend);
                if (totalOdCount === 0) return null;
                return (
                  <div className="mt-5 pt-5 border-t border-gray-100">
                    <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">On demand usage</h5>
                    <div className="space-y-2 text-sm">
                      {odInterview.count > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500">Interviews</span>
                          <span className="font-medium tabular-nums text-gray-800">{odInterview.count} {odInterview.totalSpend > 0 ? `· ${fmtINR(odInterview.totalSpend)}` : ''}</span>
                        </div>
                      )}
                      {odAssessment.count > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500">Assessments</span>
                          <span className="font-medium tabular-nums text-gray-800">{odAssessment.count} {odAssessment.totalSpend > 0 ? `· ${fmtINR(odAssessment.totalSpend)}` : ''}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center pt-1 border-t border-gray-50 font-medium">
                        <span className="text-gray-600">Total</span>
                        <span className="tabular-nums text-gray-800">{totalOdCount} {totalOdSpend > 0 ? `· ${fmtINR(totalOdSpend)}` : ''}</span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          ) : (
            <div className="space-y-3.5 text-sm">
              {[ 
                { key: 'video_interviews', label: 'Video', Icon: Video },
                { key: 'audio_interviews', label: 'Voice', Icon: Mic },
                { key: 'resume_scores', label: 'Resume', Icon: FileText },
                { key: 'assessments', label: 'Assessment', Icon: FileCheck },
              ].map(({ key, label, Icon }) => {
                const q = quotas?.[key];
                const planKey = (detail.plan || 'starter').toLowerCase();
                const defaults = planKey in QUOTA_DEFAULTS ? QUOTA_DEFAULTS[planKey] : QUOTA_DEFAULTS.starter;
                const total = q?.quota ?? defaults?.[key] ?? 0;
                const used =
                  key === 'resume_scores' && typeof detail.resumeUploadsTotal === 'number'
                    ? detail.resumeUploadsTotal
                    : q?.used ?? 0;
                const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
                const onDemandCount = q?.onDemand?.count || 0;
                return (
                  <div key={key} className="py-0.5">
                    <div className="flex justify-between items-center mb-0.5">
                      <span className="text-gray-500 flex items-center gap-1.5">
                        <Icon className="h-3.5 w-3.5 shrink-0" />
                        {label}
                        {onDemandCount > 0 && (
                          <span className="text-amber-600 text-xs">(+{onDemandCount})</span>
                        )}
                      </span>
                      <span className="font-medium tabular-nums text-gray-800 text-right">
                        {used.toLocaleString()} / {total.toLocaleString()}
                      </span>
                    </div>
                    <ProgressBar pct={pct} colorClass={pct >= 90 ? 'bg-red-500' : 'bg-[#007BFF]'} />
                  </div>
                );
              })}
              <div className="flex justify-between items-center pt-2 border-t border-gray-50 text-xs">
                <span className="text-gray-500 flex items-center gap-1.5">
                  <ScanLine className="h-3.5 w-3.5 shrink-0" /> OCR Calls
                </span>
                <span className="font-semibold tabular-nums text-gray-700">{ocrCallsDisplay}</span>
              </div>
              {(() => {
                const odItems = [
                  { key: 'video_interviews', label: 'Video' },
                  { key: 'audio_interviews', label: 'Voice' },
                  { key: 'resume_scores', label: 'Resume' },
                  { key: 'assessments', label: 'Assessment' },
                ].filter(({ key }) => (quotas?.[key]?.onDemand?.count || 0) > 0);
                const totalOdCount = odItems.reduce((s, { key }) => s + (quotas?.[key]?.onDemand?.count || 0), 0);
                const totalOdSpend = odItems.reduce((s, { key }) => s + (quotas?.[key]?.onDemand?.totalSpend || 0), 0);
                if (totalOdCount === 0) return null;
                return (
                  <div className="mt-5 pt-5 border-t border-gray-100">
                    <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">On demand usage</h5>
                    <div className="space-y-2 text-sm">
                      {odItems.map(({ key, label }) => {
                        const od = quotas?.[key]?.onDemand || {};
                        const cnt = od.count || 0;
                        const spend = od.totalSpend || 0;
                        return (
                          <div key={key} className="flex justify-between items-center">
                            <span className="text-gray-500">{label}</span>
                            <span className="font-medium tabular-nums text-gray-800">
                              {cnt.toLocaleString()} {spend > 0 ? `· ${fmtINR(spend)}` : ''}
                            </span>
                          </div>
                        );
                      })}
                      {odItems.length > 1 && (
                        <div className="flex justify-between items-center pt-1 border-t border-gray-50 font-medium">
                          <span className="text-gray-600">Total</span>
                          <span className="tabular-nums text-gray-800">
                            {totalOdCount.toLocaleString()} {totalOdSpend > 0 ? `· ${fmtINR(totalOdSpend)}` : ''}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* LLM Usage */}
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex items-center gap-2 mb-3">
            <Layers className="h-4 w-4 text-blue-500" />
            <h4 className="text-sm font-semibold text-gray-800">LLM Usage</h4>
          </div>
          <TooltipProvider delayDuration={200} skipDelayDuration={0}>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-gray-500">API Calls</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                      {(detail.llm?.callCount || 0).toLocaleString()}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-left">
                    <p className="font-medium text-gray-900 mb-1">How it&apos;s calculated</p>
                    <p className="text-gray-600">
                      Number of LLM API requests made for this organisation in the selected period. Each request to the model (e.g. OpenAI) is counted once.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Input tokens</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                      {(detail.llm?.inputTokens || 0).toLocaleString()}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-left">
                    <p className="font-medium text-gray-900 mb-1">How it&apos;s calculated</p>
                    <p className="text-gray-600">
                      Sum of prompt (input) tokens across all LLM calls. Reported by the API per request; we sum them for this organisation.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Output tokens</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                      {(detail.llm?.outputTokens || 0).toLocaleString()}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-left">
                    <p className="font-medium text-gray-900 mb-1">How it&apos;s calculated</p>
                    <p className="text-gray-600">
                      Sum of completion (output) tokens across all LLM calls. Reported by the API per request; we sum them for this organisation.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Input Words</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                      {Math.round((detail.llm?.inputTokens || 0) * 0.75).toLocaleString()}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-left">
                    <p className="font-medium text-gray-900 mb-1">How it&apos;s calculated</p>
                    <p className="text-gray-600">
                      Approximate word count for all input text. Formula: <strong>Input tokens × 0.75</strong> (standard English approximation; actual words may vary).
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Output Words</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                      {Math.round((detail.llm?.outputTokens || 0) * 0.75).toLocaleString()}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-left">
                    <p className="font-medium text-gray-900 mb-1">How it&apos;s calculated</p>
                    <p className="text-gray-600">
                      Approximate word count for all output text. Formula: <strong>Output tokens × 0.75</strong> (standard English approximation; actual words may vary).
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Total Tokens</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                      {(detail.llm?.totalTokens ?? ((detail.llm?.inputTokens || 0) + (detail.llm?.outputTokens || 0))).toLocaleString()}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-left">
                    <p className="font-medium text-gray-900 mb-1">How it&apos;s calculated</p>
                    <p className="text-gray-600">
                      Input tokens + Output tokens. Equals the total token volume consumed across all LLM calls.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Est. Spend (₹)</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                      {fmtINRDec(detail.llm?.estimatedCostInr ?? (detail.llm?.estimatedCostUsd != null ? detail.llm.estimatedCostUsd * 92 : 0))}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-left">
                    <p className="font-medium text-gray-900 mb-1">How it&apos;s calculated</p>
                    <p className="text-gray-600">
                      For each call we use the model&apos;s price per 1M tokens (input and output rates), then sum in USD and convert to <strong>INR</strong> using the configured USD→INR rate.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          </TooltipProvider>
        </div>

        {/* AI Interviews */}
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex items-center gap-2 mb-3">
            <Video className="h-4 w-4 text-emerald-500" />
            <h4 className="text-sm font-semibold text-gray-800">AI Interviews</h4>
          </div>
          <TooltipProvider delayDuration={200} skipDelayDuration={0}>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Sessions</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                      {(detail.ai?.sessionCount || 0).toLocaleString()}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-left">
                    <p className="font-medium text-gray-900 mb-1">How it&apos;s calculated</p>
                    <p className="text-gray-600">
                      Number of AI interview sessions that had real conversation: either <strong>completed</strong> or with <strong>at least 2 transcript turns</strong>. Sessions with no/minimal activity are not counted.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Total Minutes</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                      {Number(detail.ai?.totalDurationMinutes || 0).toFixed(1)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-left">
                    <p className="font-medium text-gray-900 mb-1">How it&apos;s calculated</p>
                    <p className="text-gray-600">
                      Sum of all session durations in minutes. Duration per session comes from stored analytics, or from transcript start/end timestamps, or from session start→end time.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Total Hours</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                      {(Number(detail.ai?.totalDurationMinutes || 0) / 60).toFixed(1)} hrs
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-left">
                    <p className="font-medium text-gray-900 mb-1">How it&apos;s calculated</p>
                    <p className="text-gray-600">
                      Same usage as Total Minutes, in hours. Formula: <strong>Total Minutes ÷ 60</strong>.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              {(detail.ai?.sessionCount || 0) > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Avg per session</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                        {(Number(detail.ai?.totalDurationMinutes || 0) / (detail.ai?.sessionCount || 1)).toFixed(1)} min
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-left">
                      <p className="font-medium text-gray-900 mb-1">How it&apos;s calculated</p>
                      <p className="text-gray-600">
                        Average interview length. Formula: <strong>Total Minutes ÷ Sessions</strong>.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}
              {detail.ai?.medianDurationMinutes != null && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Median duration</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                        {Number(detail.ai.medianDurationMinutes).toFixed(1)} min
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-left">
                      <p className="font-medium text-gray-900 mb-1">How it&apos;s calculated</p>
                      <p className="text-gray-600">
                        Middle value of all session durations (sorted). Half of interviews were shorter, half longer. Unlike average, not skewed by a few very long or very short sessions.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}
              {/* Voice — accordion */}
              <div className="border-t border-gray-100 pt-2 mt-1" />
              <div className="rounded border border-gray-100 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setVoiceAccordionOpen((v) => !v)}
                  className="w-full flex justify-between items-center px-2 py-1.5 text-left text-xs font-medium text-gray-600 uppercase tracking-wide hover:bg-gray-50/50 transition-colors"
                >
                  <span>
                    Voice ({detail.ai?.voiceSessionCount || 0} sessions, {Number(detail.ai?.voiceDurationMinutes || 0).toFixed(1)} min)
                  </span>
                  <span className="flex items-center gap-1.5 shrink-0">
                    <span className="font-semibold tabular-nums text-gray-900 normal-case">
                      {fmtINRDec(detail.ai?.estimatedVoiceTotalCostInr ?? (detail.ai?.estimatedVoiceTotalCostUsd != null ? detail.ai.estimatedVoiceTotalCostUsd * 92 : 0))}
                    </span>
                    {voiceAccordionOpen ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {voiceAccordionOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="pl-3 space-y-0.5 border-l-2 border-blue-100 ml-0.5 pt-1">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500 text-sm">LiveKit (₹)</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">{fmtINRDec(detail.ai?.estimatedVoiceCostInr ?? (detail.ai?.estimatedVoiceCostUsd != null ? detail.ai.estimatedVoiceCostUsd * 92 : 0))}</span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-left">
                              <p className="font-medium text-gray-900 mb-1">LiveKit (voice) cost</p>
                              <p className="text-gray-600"><strong>$0.0205/min</strong> = Agent + WebRTC + Audio Transcode + Recording. Voice Minutes × $0.0205 × USD→INR.</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500 text-sm">STT (₹)</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">{fmtINRDec(detail.ai?.estimatedVoiceSttCostInr ?? (detail.ai?.estimatedVoiceSttCostUsd != null ? detail.ai.estimatedVoiceSttCostUsd * 92 : 0))}</span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-left">
                              <p className="font-medium text-gray-900 mb-1">STT (Speech-to-Text) cost</p>
                              <p className="text-gray-600">Actual candidate speech time from transcript (when user was speaking) × <strong>$0.0077/min</strong> × USD→INR. Fallback ~50% only when transcript has no duration.</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500 text-sm">TTS (₹)</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">{fmtINRDec(detail.ai?.estimatedVoiceTtsCostInr ?? (detail.ai?.estimatedVoiceTtsCostUsd != null ? detail.ai.estimatedVoiceTtsCostUsd * 92 : 0))}</span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-left">
                              <p className="font-medium text-gray-900 mb-1">TTS (Text-to-Speech) cost</p>
                              <p className="text-gray-600">Actual AI speech time from transcript (time AI was speaking) × <strong>$0.03/min</strong> × USD→INR. (~50% fallback only when transcript has no duration.)</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500 text-sm">LLM Inference (₹)</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">{fmtINRDec(detail.ai?.estimatedVoiceLlmInferenceCostInr ?? (detail.ai?.estimatedVoiceLlmInferenceCostUsd != null ? detail.ai.estimatedVoiceLlmInferenceCostUsd * 92 : 0))}</span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-left">
                              <p className="font-medium text-gray-900 mb-1">LLM cost calculation</p>
                              <p className="text-gray-600">GPT-4.1 mini via LiveKit Inference. <strong>$0.0015/min</strong>. Voice share = (Voice Minutes ÷ Total Minutes) × Total LLM cost.</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500 text-sm">Observability (₹)</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">{fmtINRDec(detail.ai?.estimatedVoiceObservabilityCostInr ?? (detail.ai?.estimatedVoiceObservabilityCostUsd != null ? detail.ai.estimatedVoiceObservabilityCostUsd * 92 : 0))}</span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-left">
                              <p className="font-medium text-gray-900 mb-1">Observability cost</p>
                              <p className="text-gray-600">Voice minutes × <strong>$0.01/min</strong> × USD→INR.</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <div className="flex justify-between items-center pt-0.5 border-t border-gray-50">
                          <span className="text-gray-600 text-sm font-medium">Voice subtotal (₹)</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="font-semibold tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">{fmtINRDec(detail.ai?.estimatedVoiceTotalCostInr ?? (detail.ai?.estimatedVoiceTotalCostUsd != null ? detail.ai.estimatedVoiceTotalCostUsd * 92 : 0))}</span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-left">
                              <p className="font-medium text-gray-900 mb-1">Voice subtotal</p>
                              <p className="text-gray-600">LiveKit + STT + TTS + LLM + Observability for voice sessions.</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        {(detail.ai?.voiceSessionCount || 0) > 0 && (
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-gray-500">Avg cost per interview (Voice)</span>
                            <span className="font-medium tabular-nums text-gray-700">
                              {fmtINRDec((detail.ai?.estimatedVoiceTotalCostInr ?? (detail.ai?.estimatedVoiceTotalCostUsd != null ? detail.ai.estimatedVoiceTotalCostUsd * 92 : 0)) / (detail.ai?.voiceSessionCount || 1))}
                            </span>
                          </div>
                        )}
                        {Number(detail.ai?.voiceDurationMinutes || 0) > 0 && (
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-gray-500">Avg cost per min (Voice)</span>
                            <span className="font-medium tabular-nums text-gray-700">
                              {fmtINRDec((detail.ai?.estimatedVoiceTotalCostInr ?? (detail.ai?.estimatedVoiceTotalCostUsd != null ? detail.ai.estimatedVoiceTotalCostUsd * 92 : 0)) / (detail.ai?.voiceDurationMinutes || 1))}/min
                            </span>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Video — accordion */}
              <div className="rounded border border-gray-100 overflow-hidden mt-1">
                <button
                  type="button"
                  onClick={() => setVideoAccordionOpen((v) => !v)}
                  className="w-full flex justify-between items-center px-2 py-1.5 text-left text-xs font-medium text-gray-600 uppercase tracking-wide hover:bg-gray-50/50 transition-colors"
                >
                  <span>
                    Video ({detail.ai?.videoSessionCount || 0} sessions, {Number(detail.ai?.videoDurationMinutes || 0).toFixed(1)} min)
                  </span>
                  <span className="flex items-center gap-1.5 shrink-0">
                    <span className="font-semibold tabular-nums text-gray-900 normal-case">
                      {fmtINRDec(detail.ai?.estimatedVideoTotalCostInr ?? (detail.ai?.estimatedVideoTotalCostUsd != null ? detail.ai.estimatedVideoTotalCostUsd * 92 : 0))}
                    </span>
                    {videoAccordionOpen ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {videoAccordionOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="pl-3 space-y-0.5 border-l-2 border-emerald-100 ml-0.5 pt-1">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500 text-sm">LiveKit (₹)</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">{fmtINRDec(detail.ai?.estimatedVideoCostInr ?? (detail.ai?.estimatedVideoCostUsd != null ? detail.ai.estimatedVideoCostUsd * 92 : 0))}</span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-left">
                              <p className="font-medium text-gray-900 mb-1">LiveKit (video) cost</p>
                              <p className="text-gray-600"><strong>$0.0355/min</strong> = Agent + WebRTC + Video Transcode + Recording. Video Minutes × $0.0355 × USD→INR.</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500 text-sm">STT (₹)</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">{fmtINRDec(detail.ai?.estimatedVideoSttCostInr ?? (detail.ai?.estimatedVideoSttCostUsd != null ? detail.ai.estimatedVideoSttCostUsd * 92 : 0))}</span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-left">
                              <p className="font-medium text-gray-900 mb-1">STT (Speech-to-Text) cost</p>
                              <p className="text-gray-600">Actual candidate speech time from transcript (time user was speaking) × <strong>$0.0077/min</strong> × USD→INR. (~50% fallback only when transcript has no duration.)</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500 text-sm">TTS (₹)</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">{fmtINRDec(detail.ai?.estimatedVideoTtsCostInr ?? (detail.ai?.estimatedVideoTtsCostUsd != null ? detail.ai.estimatedVideoTtsCostUsd * 92 : 0))}</span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-left">
                              <p className="font-medium text-gray-900 mb-1">TTS (Text-to-Speech) cost</p>
                              <p className="text-gray-600">Actual AI speech time from transcript (time AI was speaking) × <strong>$0.03/min</strong> × USD→INR. (~50% fallback only when transcript has no duration.)</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500 text-sm">LLM Inference (₹)</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">{fmtINRDec(detail.ai?.estimatedVideoLlmInferenceCostInr ?? (detail.ai?.estimatedVideoLlmInferenceCostUsd != null ? detail.ai.estimatedVideoLlmInferenceCostUsd * 92 : 0))}</span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-left">
                              <p className="font-medium text-gray-900 mb-1">LLM cost calculation</p>
                              <p className="text-gray-600">GPT-4.1 mini via LiveKit Inference. <strong>$0.0015/min</strong>. Video share = (Video Minutes ÷ Total Minutes) × Total LLM cost.</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500 text-sm">Observability (₹)</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">{fmtINRDec(detail.ai?.estimatedVideoObservabilityCostInr ?? (detail.ai?.estimatedVideoObservabilityCostUsd != null ? detail.ai.estimatedVideoObservabilityCostUsd * 92 : 0))}</span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-left">
                              <p className="font-medium text-gray-900 mb-1">Observability cost</p>
                              <p className="text-gray-600">Video minutes × <strong>$0.01/min</strong> × USD→INR.</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <div className="flex justify-between items-center pt-0.5 border-t border-gray-50">
                          <span className="text-gray-600 text-sm font-medium">Video subtotal (₹)</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="font-semibold tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">{fmtINRDec(detail.ai?.estimatedVideoTotalCostInr ?? (detail.ai?.estimatedVideoTotalCostUsd != null ? detail.ai.estimatedVideoTotalCostUsd * 92 : 0))}</span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-left">
                              <p className="font-medium text-gray-900 mb-1">Video subtotal</p>
                              <p className="text-gray-600">LiveKit + STT + TTS + LLM + Observability for video sessions.</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        {(detail.ai?.videoSessionCount || 0) > 0 && (
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-gray-500">Avg cost per interview (Video)</span>
                            <span className="font-medium tabular-nums text-gray-700">
                              {fmtINRDec((detail.ai?.estimatedVideoTotalCostInr ?? (detail.ai?.estimatedVideoTotalCostUsd != null ? detail.ai.estimatedVideoTotalCostUsd * 92 : 0)) / (detail.ai?.videoSessionCount || 1))}
                            </span>
                          </div>
                        )}
                        {Number(detail.ai?.videoDurationMinutes || 0) > 0 && (
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-gray-500">Avg cost per min (Video)</span>
                            <span className="font-medium tabular-nums text-gray-700">
                              {fmtINRDec((detail.ai?.estimatedVideoTotalCostInr ?? (detail.ai?.estimatedVideoTotalCostUsd != null ? detail.ai.estimatedVideoTotalCostUsd * 92 : 0)) / (detail.ai?.videoDurationMinutes || 1))}/min
                            </span>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Total AI Interview Cost */}
              <div className="flex justify-between items-center border-t border-gray-100 pt-2 mt-1">
                <span className="text-gray-700 font-medium">Total AI Interview Cost (₹)</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="font-semibold tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                      {fmtINRDec(detail.ai?.estimatedTotalCostInr ?? (detail.ai?.estimatedTotalCostUsd != null ? detail.ai.estimatedTotalCostUsd * 92 : (detail.ai?.estimatedLivekitCostInr ?? (detail.ai?.estimatedLivekitCostUsd != null ? detail.ai.estimatedLivekitCostUsd * 92 : 0))))}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-left">
                    <p className="font-medium text-gray-900 mb-1">Total AI Interview cost</p>
                    <p className="text-gray-600">
                      LiveKit + STT + TTS + LLM (Inference) + Observability.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>

              {Number(detail.ai?.totalDurationMinutes || 0) > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Avg cost per minute</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                        {fmtINRDec((detail.ai?.estimatedTotalCostInr ?? (detail.ai?.estimatedTotalCostUsd ?? detail.ai?.estimatedLivekitCostUsd ?? 0) * 92) / (detail.ai?.totalDurationMinutes || 1))}/min
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-left">
                      <p className="font-medium text-gray-900 mb-1">Blended cost per minute</p>
                      <p className="text-gray-600">
                        Total AI Interview Cost (₹) ÷ Total Minutes.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}
              {(detail.ai?.sessionCount || 0) > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Avg cost per interview</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                        {fmtINRDec((detail.ai?.estimatedTotalCostInr ?? (detail.ai?.estimatedTotalCostUsd ?? detail.ai?.estimatedLivekitCostUsd ?? 0) * 92) / (detail.ai?.sessionCount || 1))}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-left">
                      <p className="font-medium text-gray-900 mb-1">Average cost per session</p>
                      <p className="text-gray-600">
                        Total AI Interview Cost (₹) ÷ Sessions.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}
            </div>
          </TooltipProvider>
        </div>

        {/* Other spending (S3, emails, etc.) – all costs in INR */}
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex items-center gap-2 mb-3">
            <DollarSign className="h-4 w-4 text-slate-500" />
            <h4 className="text-sm font-semibold text-gray-800">Other spending</h4>
          </div>
          <TooltipProvider delayDuration={200} skipDelayDuration={0}>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-gray-500">S3 storage</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                      {((detail.otherUsage?.s3BytesUsed ?? 0) / 1e9).toFixed(2)} GB
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-left">
                    <p className="font-medium text-gray-900 mb-1">How it&apos;s calculated</p>
                    <p className="text-gray-600">
                      Total size of resume files stored in S3 for this organisation (prefix <strong>resumes/&#123;org-slug&#125;/</strong>). Sum of all object sizes from the bucket.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">S3 est. cost</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                      {fmtINRDec(detail.otherUsage?.s3EstCostInr)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-left">
                    <p className="font-medium text-gray-900 mb-1">How it&apos;s calculated</p>
                    <p className="text-gray-600">
                      Estimated monthly storage cost in INR. Formula: <strong>(Storage in GB × $0.023/GB/month) × USD→INR rate</strong>. Based on AWS S3 Standard pricing.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Recording storage</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                      {((detail.otherUsage?.recordingTotalBytesUsed ?? 0) / 1e9).toFixed(2)} GB
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-left">
                    <p className="font-medium text-gray-900 mb-1">Interview recording storage</p>
                    <p className="text-gray-600">
                      Voice: <strong>{((detail.otherUsage?.recordingVoiceBytesUsed ?? 0) / 1e9).toFixed(2)} GB</strong> | Video: <strong>{((detail.otherUsage?.recordingVideoBytesUsed ?? 0) / 1e9).toFixed(2)} GB</strong><br />
                      Files stored under <strong>recordings/voice/&#123;slug&#125;/</strong> and <strong>recordings/video/&#123;slug&#125;/</strong> in S3.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Recording est. cost</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                      {fmtINRDec(detail.otherUsage?.recordingEstCostInr)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-left">
                    <p className="font-medium text-gray-900 mb-1">Recording storage cost</p>
                    <p className="text-gray-600">
                      Monthly S3 cost for interview recordings. Formula: <strong>(Recording GB × $0.025/GB/month) × USD→INR</strong>.
                      {(detail.otherUsage?.recordingObjectCount ?? 0) > 0 && <><br />{detail.otherUsage.recordingObjectCount} files stored.</>}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Emails sent</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                      {(detail.otherUsage?.emailCount ?? 0).toLocaleString()}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-left">
                    <p className="font-medium text-gray-900 mb-1">How it&apos;s calculated</p>
                    <p className="text-gray-600">
                      Number of transactional emails sent for this organisation (invites, screening, assessments, etc.). Logged per send.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Email est. cost</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                      {fmtINRDec(detail.otherUsage?.emailEstCostInr)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-left">
                    <p className="font-medium text-gray-900 mb-1">How it&apos;s calculated</p>
                    <p className="text-gray-600">
                      Estimated cost in INR. Formula: <strong>Emails sent × $0.0001/email × USD→INR rate</strong>. Based on typical transactional email pricing.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">S3 requests (GET/PUT)</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                      {(detail.otherUsage?.s3GetRequests ?? 0).toLocaleString()} GET / {(detail.otherUsage?.s3PutRequests ?? 0).toLocaleString()} PUT
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-left">
                    <p className="font-medium text-gray-900 mb-1">How it&apos;s calculated</p>
                    <p className="text-gray-600">
                      Request counts for objects under <strong>resumes/&#123;org-slug&#125;/</strong>.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">S3 request est. cost</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                      {fmtINRDec(detail.otherUsage?.s3RequestCostInr)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-left">
                    <p className="font-medium text-gray-900 mb-1">How it&apos;s calculated</p>
                    <p className="text-gray-600">
                      <strong>(GET ÷ 1,000 × $0.0004 + PUT ÷ 1,000 × $0.005) × USD→INR</strong>. AWS S3 request pricing.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Bandwidth / egress</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                      {((detail.otherUsage?.bandwidthBytesEgress ?? 0) / 1e9).toFixed(2)} GB
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-left">
                    <p className="font-medium text-gray-900 mb-1">How it&apos;s calculated</p>
                    <p className="text-gray-600">
                      Data transfer out (downloads) for this org&apos;s S3 objects.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Bandwidth est. cost</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="font-medium tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                      {fmtINRDec(detail.otherUsage?.bandwidthCostInr)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-left">
                    <p className="font-medium text-gray-900 mb-1">How it&apos;s calculated</p>
                    <p className="text-gray-600">
                      <strong>Egress (GB) × $0.09/GB × USD→INR</strong>. AWS data transfer out (first 10 TB tier).
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex justify-between items-center border-t border-gray-100 pt-2 mt-1">
                <span className="text-gray-700 font-medium">Total est. (₹)</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="font-semibold tabular-nums cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                      {fmtINRDec(detail.otherUsage?.totalEstCostInr)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-left">
                    <p className="font-medium text-gray-900 mb-1">How it&apos;s calculated</p>
                    <p className="text-gray-600">
                      Sum of S3 storage + S3 requests + Bandwidth egress + Email estimated costs (all in INR).
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          </TooltipProvider>
        </div>

        {/* Plan & Billing */}
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex items-center gap-2 mb-3">
            <CreditCard className="h-4 w-4 text-amber-500" />
            <h4 className="text-sm font-semibold text-gray-800">Plan &amp; Billing</h4>
          </div>
          <TooltipProvider delayDuration={200} skipDelayDuration={0}>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Plan</span>
                <Badge className={planColor(detail.plan)}>{capitalize(detail.plan)}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Billing Period</span>
                <span className="font-medium">{capitalize(detail.billingPeriod)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Price</span>
                <span className="font-medium tabular-nums">
                  {detail.planCurrency === 'USD' ? fmtUSD(detail.planMonthlyPrice) : fmtINR(detail.planMonthlyPrice)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Created</span>
                <span className="font-medium">{fmtDate(detail.createdAt)}</span>
              </div>
              {detail.package_expiry_date && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Expires</span>
                  <span className="font-medium">{fmtDate(detail.package_expiry_date)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">Days left for expiry</span>
                <span className="font-medium tabular-nums">
                  {detail.package_expiry_date
                    ? (() => {
                      const days = daysBetween(new Date(), detail.package_expiry_date);
                      if (days < 0) return <span className="text-red-600">Expired</span>;
                      if (days === 0) return <span className="text-amber-600">Today</span>;
                      return <span className={days <= 30 ? 'text-amber-600' : ''}>{days} days</span>;
                    })()
                    : '—'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">LLM cost (₹)</span>
                <span className="font-medium tabular-nums">
                  {fmtINRDec(detail.llm?.estimatedCostInr ?? (detail.llm?.estimatedCostUsd != null ? detail.llm.estimatedCostUsd * 92 : 0))}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Voice (₹)</span>
                <span className="font-medium tabular-nums" title="LiveKit + STT + TTS + LLM + Observability for voice sessions">
                  {fmtINRDec(detail.ai?.estimatedVoiceTotalCostInr ?? (detail.ai?.estimatedVoiceTotalCostUsd != null ? detail.ai.estimatedVoiceTotalCostUsd * 92 : 0))}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Video (₹)</span>
                <span className="font-medium tabular-nums" title="LiveKit + STT + TTS + LLM + Observability for video sessions">
                  {fmtINRDec(detail.ai?.estimatedVideoTotalCostInr ?? (detail.ai?.estimatedVideoTotalCostUsd != null ? detail.ai.estimatedVideoTotalCostUsd * 92 : 0))}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Total AI Interview cost (₹)</span>
                <span className="font-medium tabular-nums">
                  {fmtINRDec(detail.ai?.estimatedTotalCostInr ?? (detail.ai?.estimatedTotalCostUsd != null ? detail.ai.estimatedTotalCostUsd * 92 : (detail.ai?.estimatedLivekitCostInr ?? (detail.ai?.estimatedLivekitCostUsd != null ? detail.ai.estimatedLivekitCostUsd * 92 : 0))))}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Other spending (₹)</span>
                <span className="font-medium tabular-nums">{fmtINRDec(detail.otherUsage?.totalEstCostInr)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 font-medium">Total cost to company (₹)</span>
                <span className="font-semibold tabular-nums">
                  {fmtINRDec(
                    (detail.llm?.estimatedCostInr ?? (detail.llm?.estimatedCostUsd != null ? detail.llm.estimatedCostUsd * 92 : 0)) +
                    (detail.ai?.estimatedTotalCostInr ?? (detail.ai?.estimatedTotalCostUsd ?? detail.ai?.estimatedLivekitCostUsd) != null
                      ? ((detail.ai?.estimatedTotalCostUsd ?? detail.ai?.estimatedLivekitCostUsd) * 92)
                      : (detail.ai?.estimatedLivekitCostInr ?? (detail.ai?.estimatedLivekitCostUsd != null ? detail.ai.estimatedLivekitCostUsd * 92 : 0))) +
                    (detail.otherUsage?.totalEstCostInr ?? 0)
                  )}
                </span>
              </div>
            </div>
          </TooltipProvider>
        </div>
      </div>
    </>
  );
}

function TeamsTab({ detail }) {
  const [expandedTeam, setExpandedTeam] = useState(null);
  const teams = detail.teams || [];

  if (!teams.length) return <EmptyState text="No teams created yet" />;

  return (
    <div className="space-y-3">
      {teams.map((team) => {
        const isOpen = expandedTeam === team._id;
        return (
          <div key={team._id} className="rounded-xl border border-gray-200 bg-white overflow-hidden">
            <button
              onClick={() => setExpandedTeam(isOpen ? null : team._id)}
              className="flex w-full items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Users className="h-4 w-4 text-indigo-500" />
                <span className="text-sm font-medium text-gray-800">{team.name}</span>
                <Badge className="bg-gray-100 text-gray-600">{(team.members || []).length} members</Badge>
              </div>
              {isOpen ? (
                <ChevronUp className="h-4 w-4 text-gray-400" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-400" />
              )}
            </button>
            <AnimatePresence>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden"
                >
                  <div className="px-5 pb-4">
                    {(team.members || []).length === 0 ? (
                      <p className="text-sm text-gray-400 py-3">No members in this team</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                              <th className="pb-2 pr-4 font-medium">Name</th>
                              <th className="pb-2 pr-4 font-medium">Email</th>
                              <th className="pb-2 pr-4 font-medium">Role</th>
                              <th className="pb-2 pr-4 font-medium">Status</th>
                              <th className="pb-2 font-medium">Joined</th>
                            </tr>
                          </thead>
                          <tbody>
                            {team.members.map((m, idx) => (
                              <tr key={m._id || idx} className="border-b border-gray-50 hover:bg-gray-50">
                                <td className="py-2 pr-4 text-gray-800">{m.name || `${m.firstName || ''} ${m.lastName || ''}`.trim() || '—'}</td>
                                <td className="py-2 pr-4 text-gray-600">{m.email || '—'}</td>
                                <td className="py-2 pr-4">
                                  <Badge className="bg-gray-100 text-gray-600">{capitalize(m.role)}</Badge>
                                </td>
                                <td className="py-2 pr-4">
                                  <Badge className={statusColor(m.status)}>{capitalize(m.status)}</Badge>
                                </td>
                                <td className="py-2 text-gray-500 tabular-nums">{fmtDate(m.createdAt || m.joinedAt)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}

function ScreeningProctoringBadge({ proctoringRiskScore, interviewProctoringRiskScore, assessmentProctoringRiskScore }) {
  if (
    proctoringRiskScore == null &&
    interviewProctoringRiskScore == null &&
    assessmentProctoringRiskScore == null
  ) {
    return <span className="text-gray-400">—</span>;
  }
  const { label, classes } = getCombinedScreeningProctoringBadge(
    interviewProctoringRiskScore,
    assessmentProctoringRiskScore,
  );
  const n = Math.floor(
    proctoringRiskScore ??
    Math.max(interviewProctoringRiskScore ?? 0, assessmentProctoringRiskScore ?? 0),
  );
  return <Badge className={classes}>{label} · {n}</Badge>;
}

function FeedbackStars({ stars, feedback, feedbackAt }) {
  const [expanded, setExpanded] = React.useState(false);
  if (stars == null || stars < 1) return <span className="text-gray-300">—</span>;
  const LIMIT = 60;
  const hasMore = feedback && feedback.length > LIMIT;
  return (
    <div className="max-w-[180px]">
      <span className="inline-flex items-center gap-0.5 cursor-default">
        {[1, 2, 3, 4, 5].map((n) => (
          <Star key={n} className={cn('h-4 w-4', n <= stars ? 'text-amber-400 fill-amber-400' : 'text-gray-200')} />
        ))}
      </span>
      {feedback && (
        <p className="text-[11px] text-gray-500 mt-0.5 leading-snug break-words">
          {expanded ? feedback : feedback.slice(0, LIMIT)}{!expanded && hasMore ? '…' : ''}
          {hasMore && (
            <button
              onClick={() => setExpanded(p => !p)}
              className="ml-1 text-[11px] text-indigo-500 hover:underline font-medium bg-transparent border-none p-0 cursor-pointer"
            >
              {expanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </p>
      )}
    </div>
  );
}

function ScoreCell({ score, eyeIcon, candidate, onClick }) {
  if (score == null) return <span className="text-gray-400 text-center block">—</span>;
  const rounded = Math.round(score);
  const isClickable = onClick && candidate;

  const ScoreContent = (
    <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-indigo-700 bg-indigo-50 font-medium tabular-nums text-xs">
      {rounded}%
      {(eyeIcon || isClickable) && <Eye className="h-3 w-3 text-indigo-400" />}
    </span>
  );

  if (isClickable) {
    return (
      <button
        onClick={() => onClick(candidate)}
        className="hover:bg-indigo-100 rounded transition-colors cursor-pointer"
        title="View score details"
      >
        {ScoreContent}
      </button>
    );
  }

  return ScoreContent;
}

const formatAppliedDateDisplay = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const diffDays = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 30) return `${diffDays} days ago`;
  const weeks = Math.floor(diffDays / 7);
  if (weeks === 1) return '1 week ago';
  if (weeks < 12) return `${weeks} weeks ago`;
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatDateTimeDisplay = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatExperienceDisplay = (value) => {
  if (value == null) return '—';
  const numeric = typeof value === 'number' ? value : parseFloat(value, 10);
  if (Number.isNaN(numeric)) return String(value) || '—';
  const rounded = Math.round(numeric);
  return `${rounded} yr${rounded === 1 ? '' : 's'}`;
};

const formatSkillsDisplay = (skills) => {
  if (Array.isArray(skills) && skills.length) {
    return skills.filter(Boolean).slice(0, 3).join(', ') || '—';
  }
  if (typeof skills === 'string' && skills.trim()) {
    return skills.trim();
  }
  return '—';
};

const formatTextValue = (value) => {
  if (value == null) return '—';
  const str = String(value).trim();
  return str ? str : '—';
};

function CandidatesList({ screeningId, screeningName, onBack, isPlatformSuperAdmin, handleResumeScoreClick }) {
  const [candidates, setCandidates] = useState([]);
  const [meta, setMeta] = useState({ screening: null, assessment: null });
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('overallScore');
  const [sortDir, setSortDir] = useState('desc');

  const [showCandidateInsightModal, setShowCandidateInsightModal] = useState(false);
  const [candidateInsightTab, setCandidateInsightTab] = useState('score');
  const [candidateInsightData, setCandidateInsightData] = useState(null);

  // Assessment modal state
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [assessmentModalCandidate, setAssessmentModalCandidate] = useState(null);
  const [assessmentDetails, setAssessmentDetails] = useState(null);
  const [assessmentProctoringDetails, setAssessmentProctoringDetails] = useState(null);
  const [assessmentModalTab, setAssessmentModalTab] = useState('score');

  // Interview modal state
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [interviewSession, setInterviewSession] = useState(null);
  const [interviewDetails, setInterviewDetails] = useState(null);

  const handleAssessmentScoreClick = async (candidate) => {
    if (!isPlatformSuperAdmin) return;
    setAssessmentModalCandidate(candidate);
    setAssessmentDetails(null);
    setAssessmentProctoringDetails(null);
    setAssessmentModalTab('score');
    setShowAssessmentModal(true);
    try {
      const assessmentId = candidate.assessmentId || meta.screening?.assessmentId;
      if (!assessmentId || !candidate.email) {
        setAssessmentDetails({ error: true, message: 'Missing assessment or candidate email.' });
        return;
      }
      const resp = await axios.get(`/api/assessments/${assessmentId}/results/${encodeURIComponent(candidate.email)}`);
      setAssessmentDetails(resp.data);
    } catch {
      setAssessmentDetails({ error: true, message: 'Failed to load assessment details.' });
    }
  };

  const loadProctoringDetails = async (candidate) => {
    if (assessmentProctoringDetails) return;
    try {
      const proctoringSessionId = candidate.proctoringSessionId;
      if (!proctoringSessionId) {
        setAssessmentProctoringDetails({ error: true, message: 'No proctoring session found for this candidate.' });
        return;
      }
      const resp = await axios.get(`/api/internal/proctoring-session/${proctoringSessionId}`);
      setAssessmentProctoringDetails(resp.data?.session || resp.data);
    } catch {
      setAssessmentProctoringDetails({ error: true, message: 'Failed to load proctoring details.' });
    }
  };

  const handleInterviewScoreClick = async (candidate) => {
    if (!isPlatformSuperAdmin) return;
    setInterviewSession(null);
    setInterviewDetails(null);
    setShowInterviewModal(true);
    try {
      const sessionId = candidate.interviewSessionId;
      if (!sessionId) {
        toast.error('No interview session found for this candidate.');
        setShowInterviewModal(false);
        return;
      }
      const resp = await axios.get(`/api/ai-interviews/session/${sessionId}/replay`);
      const sessionData = resp.data.session || resp.data;
      setInterviewSession(sessionData);
      setInterviewDetails(sessionData.interview || null);
    } catch {
      toast.error('Failed to load interview session.');
      setShowInterviewModal(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    axios.get(`/api/internal/screening/${screeningId}/candidates`)
      .then(({ data }) => {
        setCandidates(data.candidates || []);
        setMeta({ screening: data.screening, assessment: data.assessment });
      })
      .catch(() => toast.error('Failed to load candidates'))
      .finally(() => setLoading(false));
  }, [screeningId]);

  const sorted = useMemo(() => {
    const arr = [...candidates];
    arr.sort((a, b) => {
      const av = a[sortKey] ?? -1;
      const bv = b[sortKey] ?? -1;
      return sortDir === 'desc' ? bv - av : av - bv;
    });
    return arr;
  }, [candidates, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortKey(key); setSortDir('desc'); }
  };

  const screeningMeta = meta.screening || {};
  const layout = useMemo(
    () => getCandidatesTableLayout(screeningMeta, { showStatus: true, showShortlistedDate: false }),
    [screeningMeta]
  );

  const {
    showOverallScoreColumn,
    showResumeScoreColumn,
    showAssessmentScoreColumn,
    showInterviewScoreColumn,
    showProctoringColumn,
    showCandidateDetailsColumn,
    showCompanyNameColumn,
    showDesignationColumn,
    showLocationColumn,
    showIndustryColumn,
    showExperienceColumn,
    showLatestEducationColumn,
    showSkillsColumn,
    showEmailColumn,
    showPhoneColumn,
    showAppliedDateColumn,
    showCompletedAtColumn,
  } = layout;

  const preferredSortKey = useMemo(() => {
    if (showOverallScoreColumn) return 'overallScore';
    if (showResumeScoreColumn) return 'resumeScore';
    if (showAssessmentScoreColumn) return 'assessmentPct';
    if (showInterviewScoreColumn) return 'interviewScore';
    return 'status';
  }, [showOverallScoreColumn, showResumeScoreColumn, showAssessmentScoreColumn, showInterviewScoreColumn]);

  const isColumnVisible = useCallback(
    (key) => {
      if (key === 'overallScore') return showOverallScoreColumn;
      if (key === 'resumeScore') return showResumeScoreColumn;
      if (key === 'assessmentPct') return showAssessmentScoreColumn;
      if (key === 'interviewScore') return showInterviewScoreColumn;
      return true;
    },
    [showOverallScoreColumn, showResumeScoreColumn, showAssessmentScoreColumn, showInterviewScoreColumn]
  );

  useEffect(() => {
    setSortKey((prev) => (isColumnVisible(prev) ? prev : preferredSortKey));
  }, [isColumnVisible, preferredSortKey]);

  const handleCandidateDetailsClick = useCallback(
    (candidate) => {
      if (!candidate) return;
      setCandidateInsightData(candidate);
      setCandidateInsightTab('score');
      setShowCandidateInsightModal(true);
    },
    []
  );

  const metaColumns = useMemo(() => {
    const columns = [
      {
        id: 'candidateDetails',
        label: 'Candidate Details',
        show: showCandidateDetailsColumn,
        render: (candidate) =>
          isPlatformSuperAdmin ? (
            <button
              type="button"
              onClick={() => handleCandidateDetailsClick(candidate)}
              className="inline-flex items-center justify-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-100"
            >
              <span>View</span>
              <Eye className="h-3.5 w-3.5" />
            </button>
          ) : (
            <span className="text-gray-400">—</span>
          ),
      },
      {
        id: 'company',
        label: 'Company',
        show: showCompanyNameColumn,
        render: (candidate) => formatTextValue(candidate.company),
      },
      {
        id: 'designation',
        label: 'Designation',
        show: showDesignationColumn,
        render: (candidate) => formatTextValue(candidate.designation),
      },
      {
        id: 'location',
        label: 'Location',
        show: showLocationColumn,
        render: (candidate) => formatTextValue(candidate.location || candidate.currentLocation),
      },
      {
        id: 'industry',
        label: 'Industry',
        show: showIndustryColumn,
        render: (candidate) => formatTextValue(candidate.industry),
      },
      {
        id: 'experience',
        label: 'Experience',
        show: showExperienceColumn,
        render: (candidate) => formatExperienceDisplay(candidate.experienceYears ?? candidate.experience),
      },
      {
        id: 'latestEducation',
        label: 'Latest Education',
        show: showLatestEducationColumn,
        render: (candidate) => formatTextValue(candidate.latestEducation || candidate.education),
      },
      {
        id: 'skills',
        label: 'Skills',
        show: showSkillsColumn,
        render: (candidate) => formatSkillsDisplay(candidate.skills),
      },
      {
        id: 'email',
        label: 'Email',
        show: showEmailColumn,
        render: (candidate) => formatTextValue(candidate.email),
      },
      {
        id: 'phone',
        label: 'Phone',
        show: showPhoneColumn,
        render: (candidate) => formatTextValue(candidate.phone || candidate.phoneNumber || candidate.mobile),
      },
      {
        id: 'applied',
        label: 'Applied',
        show: showAppliedDateColumn,
        render: (candidate) => formatAppliedDateDisplay(candidate.appliedAt || candidate.invitedAt || candidate.createdAt),
      },
      {
        id: 'completed',
        label: 'Completed At',
        show: showCompletedAtColumn,
        render: (candidate) => formatDateTimeDisplay(candidate.completedAt),
      },
    ];
    return columns.filter((col) => col.show);
  }, [
    handleCandidateDetailsClick,
    isPlatformSuperAdmin,
    showAppliedDateColumn,
    showCandidateDetailsColumn,
    showCompanyNameColumn,
    showCompletedAtColumn,
    showDesignationColumn,
    showEmailColumn,
    showExperienceColumn,
    showIndustryColumn,
    showLatestEducationColumn,
    showLocationColumn,
    showPhoneColumn,
    showSkillsColumn,
  ]);

  if (loading) return <div className="flex items-center justify-center py-12"><Spinner className="h-5 w-5" /></div>;

  const thCls = 'pb-3 px-4 font-semibold text-[11px] uppercase tracking-wider cursor-pointer select-none hover:text-[#007BFF] transition-colors whitespace-nowrap';
  const SortArrow = ({ k }) => sortKey === k ? <span className="ml-0.5 text-[#007BFF]">{sortDir === 'desc' ? '▼' : '▲'}</span> : null;

  return (
    <div>
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-[#007BFF] hover:text-blue-700 mb-4 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to Screenings
      </button>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <h4 className="text-sm font-semibold text-gray-800">{screeningName}</h4>
        <Badge className="bg-gray-100 text-gray-600">{candidates.length} candidates</Badge>
        {meta.assessment && <Badge className="bg-blue-50 text-blue-600">Assessment: {meta.assessment.title}</Badge>}
      </div>
      {candidates.length === 0 ? <EmptyState text="No candidates in this screening yet" /> : (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#007BFF] text-white text-left">
                  <th className="py-3 px-4 font-semibold text-[11px] uppercase tracking-wider whitespace-nowrap">Candidate</th>
                  <th className={thCls} onClick={() => toggleSort('status')} style={{ color: 'white' }}>Status<SortArrow k="status" /></th>
                  {showOverallScoreColumn && (
                    <th className={thCls} onClick={() => toggleSort('overallScore')} style={{ color: 'white' }}>
                      Overall Score
                      <SortArrow k="overallScore" />
                    </th>
                  )}
                  {showResumeScoreColumn && (
                    <th className={thCls} onClick={() => toggleSort('resumeScore')} style={{ color: 'white' }}>
                      Resume Score
                      <SortArrow k="resumeScore" />
                    </th>
                  )}
                  {showAssessmentScoreColumn && (
                    <th className={thCls} onClick={() => toggleSort('assessmentPct')} style={{ color: 'white' }}>
                      Assessment Score
                      <SortArrow k="assessmentPct" />
                    </th>
                  )}
                  {showInterviewScoreColumn && (
                    <th className={thCls} onClick={() => toggleSort('interviewScore')} style={{ color: 'white' }}>
                      Interview Score
                      <SortArrow k="interviewScore" />
                    </th>
                  )}
                  {showProctoringColumn && (
                    <th className="py-3 px-4 font-semibold text-[11px] uppercase tracking-wider whitespace-nowrap">Proctoring</th>
                  )}
                  {metaColumns.map((col) => (
                    <th key={col.id} className="py-3 px-4 font-semibold text-[11px] uppercase tracking-wider whitespace-nowrap">
                      {col.label}
                    </th>
                  ))}
                  <th className="py-3 px-4 font-semibold text-[11px] uppercase tracking-wider whitespace-nowrap">Candidate Feedback</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((c, idx) => (
                  <tr key={c._id} className={cn('border-b border-gray-100 hover:bg-blue-50/30 transition-colors', idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40')}>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">

                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate max-w-[160px]">{c.name || '—'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <Badge className={c.status === 'Completed' ? 'bg-emerald-100 text-emerald-700' : c.status === 'In Progress' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}>{c.status}</Badge>
                    </td>
                    {showOverallScoreColumn && (
                      <td className="py-3 px-4 whitespace-nowrap text-center">
                        <ScoreCell score={c.overallScore} />
                      </td>
                    )}
                    {showResumeScoreColumn && (
                      <td className="py-3 px-4 whitespace-nowrap text-center">
                        <ScoreCell
                          score={c.resumeScore}
                          candidate={c}
                          onClick={isPlatformSuperAdmin ? handleResumeScoreClick : null}
                        />
                      </td>
                    )}
                    {showAssessmentScoreColumn && (
                      <td className="py-3 px-4 whitespace-nowrap text-center">
                        <ScoreCell
                          score={c.assessmentPct}
                          candidate={c}
                          onClick={isPlatformSuperAdmin ? handleAssessmentScoreClick : null}
                        />
                      </td>
                    )}
                    {showInterviewScoreColumn && (
                      <td className="py-3 px-4 whitespace-nowrap text-center">
                        <ScoreCell
                          score={c.interviewScore}
                          candidate={c}
                          onClick={isPlatformSuperAdmin ? handleInterviewScoreClick : null}
                        />
                      </td>
                    )}
                    {showProctoringColumn && (
                      <td className="py-3 px-4 whitespace-nowrap">
                        <ScreeningProctoringBadge
                          proctoringRiskScore={c.proctoringRiskScore}
                          interviewProctoringRiskScore={c.interviewProctoringRiskScore}
                          assessmentProctoringRiskScore={c.assessmentProctoringRiskScore}
                        />
                      </td>
                    )}
                    {metaColumns.map((col) => (
                      <td key={col.id} className="py-3 px-4 whitespace-nowrap text-sm text-center">
                        {col.render(c)}
                      </td>
                    ))}
                    <td className="py-3 px-4"><FeedbackStars stars={c.feedbackStars} feedback={c.feedback} feedbackAt={c.feedbackAt} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {showCandidateInsightModal && candidateInsightData && (
        <CandidateInsightModal
          isOpen={showCandidateInsightModal}
          candidate={candidateInsightData}
          initialTab={candidateInsightTab}
          onClose={() => {
            setShowCandidateInsightModal(false);
            setCandidateInsightData(null);
          }}
        />
      )}
      {/* Assessment Score Modal */}
      {showAssessmentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[2px] p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Candidate Results</h3>
                {assessmentModalCandidate && (
                  <p className="text-sm text-gray-500">{assessmentModalCandidate.name || assessmentModalCandidate.email}</p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
                  <button
                    onClick={() => setAssessmentModalTab('score')}
                    className={`px-3 py-1.5 text-sm font-semibold ${assessmentModalTab === 'score'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                  >
                    Assessment Score
                  </button>
                  <button
                    onClick={() => {
                      setAssessmentModalTab('proctoring');
                      loadProctoringDetails(assessmentModalCandidate);
                    }}
                    className={`px-3 py-1.5 text-sm font-semibold border-l border-gray-200 ${assessmentModalTab === 'proctoring'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                  >
                    Proctoring Details
                  </button>
                </div>
                <button onClick={() => setShowAssessmentModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="px-6 py-4 overflow-y-auto flex-1">
              {assessmentModalTab === 'score' ? (
                <CandidateAssessmentScoreContent
                  details={assessmentDetails}
                  totalPointsFallback={meta.assessment?.totalPoints}
                />
              ) : (
                <CandidateProctoringDetailsContent
                  session={assessmentProctoringDetails}
                  candidate={assessmentModalCandidate}
                />
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
              <button onClick={() => setShowAssessmentModal(false)} className="px-4 py-2 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200">Close</button>
            </div>
          </div>
        </div>
      )}
      {/* Interview Session Modal */}
      {showInterviewModal && interviewSession && (
        <InterviewSessionModal
          session={interviewSession}
          interviewDetails={interviewDetails}
          initialTab="analysis"
          onClose={() => { setShowInterviewModal(false); setInterviewSession(null); }}
        />
      )}
    </div>
  );
}

function ScreeningDetailsModal({ screeningId, screeningName, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    setLoading(true);
    axios.get(`/api/internal/screening/${screeningId}/detail`)
      .then(({ data }) => { setData(data); setActiveTab(data.assessment ? 'assessment' : data.interview ? 'interview' : 'overview'); })
      .catch(() => toast.error('Failed to load screening details'))
      .finally(() => setLoading(false));
  }, [screeningId]);

  const tabs = [
    ...(data?.screening?.hasResumeScoring ? [{ key: 'resume', label: 'Resume Scoring' }] : []),
    ...(data?.assessment ? [{ key: 'assessment', label: 'Assessment' }] : []),
    ...(data?.interview ? [{ key: 'interview', label: 'AI Interview' }] : []),
  ];

  const qTypeLabel = (type) => {
    const m = { 'multiple-choice': 'MCQ', 'true-false': 'True/False', 'short-answer': 'Short Answer', essay: 'Essay', code: 'Code' };
    return m[type] || type;
  };
  const sectionLabel = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ') : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Screening Details</p>
            <h3 className="text-lg font-semibold text-gray-900">{screeningName}</h3>
            {data?.screening?.description && <p className="text-xs text-gray-500 mt-0.5">{data.screening.description}</p>}
          </div>
          <button onClick={onClose} className="ml-4 text-gray-400 hover:text-gray-600 shrink-0">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Tabs */}
        {!loading && tabs.length > 0 && (
          <div className="flex border-b border-gray-200 px-6 gap-1 bg-gray-50">
            {tabs.map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                className={cn('px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px', activeTab === t.key ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700')}>
                {t.label}
              </button>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Spinner className="h-6 w-6" /></div>
          ) : !data ? (
            <EmptyState text="Failed to load screening details" />
          ) : (
            <>
              {/* Resume Scoring tab */}
              {activeTab === 'resume' && (
                <div className="space-y-4">
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <p className="text-sm font-semibold text-gray-700 mb-1">AI Resume Scoring</p>
                    <p className="text-xs text-gray-500">AI evaluates each candidate's resume against the job requirements and assigns a score.</p>
                  </div>
                  {data.screening.resumeScoringConfig?.criteria?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Scoring Criteria</p>
                      <div className="space-y-2">
                        {data.screening.resumeScoringConfig.criteria.map((c, i) => (
                          <div key={i} className="flex items-start gap-2 rounded-lg border border-gray-100 bg-white px-3 py-2.5">
                            <span className="text-xs font-bold text-indigo-600 mt-0.5 shrink-0">#{i + 1}</span>
                            <div className="min-w-0">
                              <p className="text-sm text-gray-800">{c.text}</p>
                              {c.dimension && <p className="text-[11px] text-gray-400 mt-0.5">{c.dimension}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Assessment tab */}
              {activeTab === 'assessment' && data.assessment && (
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-3 text-xs text-gray-500 bg-gray-50 rounded-xl border border-gray-200 px-4 py-3">
                    <span>Title: <strong className="text-gray-700">{data.assessment.title}</strong></span>
                    {data.assessment.questionType && <span>Type: <strong className="text-gray-700">{data.assessment.questionType}</strong></span>}
                    <span>Questions: <strong className="text-gray-700">{data.assessment.noOfQuestions ?? data.assessment.questions?.length ?? 0}</strong></span>
                    <span>Total Points: <strong className="text-gray-700">{data.assessment.totalPoints ?? '—'}</strong></span>
                    <Badge className={data.assessment.status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}>{capitalize(data.assessment.status || '')}</Badge>
                  </div>
                  {data.assessment.questions?.length > 0 ? (
                    <div className="space-y-3">
                      {data.assessment.questions.map((q, i) => (
                        <div key={q._id || i} className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                          <div className="px-4 py-3 flex items-start gap-3">
                            <span className="shrink-0 w-6 h-6 rounded-full bg-indigo-50 text-indigo-700 text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{qTypeLabel(q.type)}</span>
                                <span className="text-[11px] text-gray-400">{q.points} pt{q.points !== 1 ? 's' : ''}</span>
                                {q.skills?.length > 0 && q.skills.map((s, si) => <span key={si} className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600">{s}</span>)}
                              </div>
                              <p className="text-sm text-gray-800 font-medium">{q.content || q.title}</p>
                              {/* MCQ options */}
                              {q.options?.length > 0 && (
                                <div className="mt-2 space-y-1">
                                  {q.options.map((opt, oi) => (
                                    <div key={oi} className={cn('flex items-center gap-2 text-sm px-2.5 py-1.5 rounded-lg', opt.isCorrect ? 'bg-emerald-50 text-emerald-800 font-medium' : 'text-gray-600')}>
                                      <span className="shrink-0 w-4 h-4 rounded-full border flex items-center justify-center text-[10px] font-bold" style={opt.isCorrect ? { background: '#059669', color: 'white', border: 'none' } : { borderColor: '#d1d5db' }}>{String.fromCharCode(65 + oi)}</span>
                                      {opt.text}
                                      {opt.isCorrect && <span className="ml-auto text-[10px] font-semibold text-emerald-600">✓ Correct</span>}
                                    </div>
                                  ))}
                                </div>
                              )}
                              {/* Correct answer for non-MCQ */}
                              {!q.options?.length && q.correctAnswer && (
                                <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2">
                                  <p className="text-[11px] font-semibold text-emerald-700 mb-0.5">Expected Answer</p>
                                  <p className="text-sm text-emerald-800">{q.correctAnswer}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState text="No questions found for this assessment" />
                  )}
                </div>
              )}

              {/* Interview tab */}
              {activeTab === 'interview' && data.interview && (
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-3 text-xs text-gray-500 bg-gray-50 rounded-xl border border-gray-200 px-4 py-3">
                    <span>Title: <strong className="text-gray-700">{data.interview.title}</strong></span>
                    {data.interview.role && <span>Role: <strong className="text-gray-700">{data.interview.role}</strong></span>}
                    <span>Type: <strong className="text-gray-700">{capitalize(data.interview.type || 'voice')}</strong></span>
                    <span>Questions: <strong className="text-gray-700">{data.interview.questions?.length ?? 0}</strong></span>
                    {data.interview.config?.duration && <span>Duration: <strong className="text-gray-700">{data.interview.config.duration} min</strong></span>}
                    {data.interview.config?.difficulty && <Badge className="bg-gray-100 text-gray-600">{capitalize(data.interview.config.difficulty)}</Badge>}
                  </div>
                  {data.interview.context && (
                    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Context / JD</p>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{data.interview.context}</p>
                    </div>
                  )}
                  {data.interview.questions?.length > 0 ? (
                    <div className="space-y-3">
                      {data.interview.questions.map((q, i) => (
                        <div key={i} className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                          <div className="px-4 py-3 flex items-start gap-3">
                            <span className="shrink-0 w-6 h-6 rounded-full bg-violet-50 text-violet-700 text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                {q.section && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-violet-50 text-violet-700">{sectionLabel(q.section)}</span>}
                                {q.type === 'yes_no' && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-orange-50 text-orange-600">Yes/No</span>}
                                {q.critical && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-600">Critical</span>}
                              </div>
                              <p className="text-sm text-gray-800 font-medium">{q.prompt}</p>
                              {q.expectedAnswer && (
                                <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2">
                                  <p className="text-[11px] font-semibold text-emerald-700 mb-0.5">Expected Answer</p>
                                  <p className="text-sm text-emerald-800">{q.expectedAnswer}</p>
                                </div>
                              )}
                              {q.expectedPoints?.length > 0 && (
                                <div className="mt-2 rounded-lg bg-blue-50 border border-blue-100 px-3 py-2">
                                  <p className="text-[11px] font-semibold text-blue-700 mb-1.5">Expected Points</p>
                                  <ul className="space-y-1">
                                    {q.expectedPoints.map((pt, pi) => (
                                      <li key={pi} className="text-sm text-blue-800 flex items-start gap-1.5">
                                        <span className="shrink-0 text-blue-400 mt-0.5">•</span>{pt}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState text="No questions found for this interview" />
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">Close</button>
        </div>
      </div>
    </div>
  );
}

function ScreeningsList({ jobId, jobTitle, onBack, isPlatformSuperAdmin, handleResumeScoreClick }) {
  const [screenings, setScreenings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedScreening, setSelectedScreening] = useState(null);
  const [detailScreening, setDetailScreening] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`/api/internal/job/${jobId}/screenings`)
      .then(({ data }) => setScreenings(data.screenings || []))
      .catch(() => toast.error('Failed to load screenings'))
      .finally(() => setLoading(false));
  }, [jobId]);

  if (selectedScreening) {
    return (
      <CandidatesList
        screeningId={selectedScreening._id}
        screeningName={selectedScreening.name}
        onBack={() => setSelectedScreening(null)}
        isPlatformSuperAdmin={isPlatformSuperAdmin}
        handleResumeScoreClick={handleResumeScoreClick}
      />
    );
  }

  if (loading) return <div className="flex items-center justify-center py-12"><Spinner className="h-5 w-5" /></div>;

  return (
    <div>
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-[#007BFF] hover:text-blue-700 mb-4 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to Job Postings
      </button>
      <div className="flex items-center gap-3 mb-4">
        <h4 className="text-sm font-semibold text-gray-800">{jobTitle}</h4>
        <Badge className="bg-gray-100 text-gray-600">{screenings.length} screenings</Badge>
      </div>
      {detailScreening && (
        <ScreeningDetailsModal
          screeningId={detailScreening._id}
          screeningName={detailScreening.name}
          onClose={() => setDetailScreening(null)}
        />
      )}
      {screenings.length === 0 ? <EmptyState text="No screenings created for this job" /> : (
        <div className="space-y-3">
          {screenings.map((s) => (
            <div key={s._id} className="rounded-xl border border-gray-200 bg-white hover:shadow-sm hover:border-[#007BFF]/30 transition-all group">
              <button onClick={() => setSelectedScreening(s)} className="w-full text-left px-5 pt-4 pb-3">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-gray-900 group-hover:text-[#007BFF] transition-colors">{s.name}</span>
                      {s.applyLinkActive && <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>}
                      {!s.applyLinkActive && <Badge className="bg-gray-100 text-gray-500">Inactive</Badge>}
                    </div>
                    {s.description && <p className="text-xs text-gray-500 line-clamp-1 mb-1">{s.description}</p>}
                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                      {s.hasResumeScoring && <span className="inline-flex items-center gap-1"><FileText className="h-3 w-3" /> Resume Scoring</span>}
                      {s.includeAssessment && <span className="inline-flex items-center gap-1"><FileCheck className="h-3 w-3" /> Assessment</span>}
                      {s.includeInterview && <span className="inline-flex items-center gap-1"><Video className="h-3 w-3" /> Interview</span>}
                      <span className="tabular-nums">{fmtDate(s.createdAt)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0 ml-4">
                    <div className="text-center"><p className="text-lg font-semibold text-gray-900 tabular-nums">{s.candidates}</p><p className="text-[10px] text-gray-500">Candidates</p></div>
                    <div className="text-center"><p className="text-lg font-semibold text-emerald-600 tabular-nums">{s.shortlisted}</p><p className="text-[10px] text-gray-500">Selected</p></div>
                    <div className="text-center"><p className="text-lg font-semibold text-blue-600 tabular-nums">{s.completed}</p><p className="text-[10px] text-gray-500">Completed</p></div>
                    <ChevronDown className="h-4 w-4 text-gray-300 -rotate-90 group-hover:text-[#007BFF] transition-colors" />
                  </div>
                </div>
              </button>
              <div className="px-5 pb-3 border-t border-gray-50 pt-2 flex items-center gap-2">
                <button
                  onClick={() => setDetailScreening(s)}
                  className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-700 hover:underline bg-transparent border-none p-0 cursor-pointer"
                >
                  View screening details
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function JDModal({ job, onClose }) {
  if (!job) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200">
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-gray-900 truncate">{job.title}</h3>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              {job.location && <span className="text-xs text-gray-500">{job.location}</span>}
              {job.locationType && <Badge className="bg-gray-100 text-gray-600">{capitalize(job.locationType)}</Badge>}
              <Badge className={statusColor(job.status)}>{capitalize(job.status)}</Badge>
            </div>
          </div>
          <button onClick={onClose} className="ml-4 text-gray-400 hover:text-gray-600 transition-colors shrink-0">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        {/* Body */}
        <div className="overflow-y-auto px-6 py-5 space-y-5 flex-1">
          {/* Meta row */}
          <div className="flex flex-wrap gap-4 text-xs text-gray-500">
            {(job.experienceMin != null || job.experienceMax != null) && (
              <span>Experience: <strong className="text-gray-700">{[job.experienceMin, job.experienceMax].filter(v => v != null).join('–')} yrs</strong></span>
            )}
            {job.candidateQualification && (
              <span>Qualification: <strong className="text-gray-700">{job.candidateQualification}</strong></span>
            )}
            {job.noticePeriod && (
              <span>Notice: <strong className="text-gray-700">{job.noticePeriod}</strong></span>
            )}
            {(job.salaryMin != null || job.salaryMax != null) && (
              <span>Salary: <strong className="text-gray-700">{[job.salaryMin, job.salaryMax].filter(v => v != null).map(v => v.toLocaleString()).join('–')} {job.salaryCurrency || ''}</strong></span>
            )}
          </div>
          {/* Skills */}
          {Array.isArray(job.skills) && job.skills.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Skills</p>
              <div className="flex flex-wrap gap-1.5">
                {job.skills.map((s, i) => (
                  <span key={i} className="px-2.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-xs font-medium">{s}</span>
                ))}
              </div>
            </div>
          )}
          {/* Description */}
          {job.description && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Overview</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{job.description}</p>
            </div>
          )}
          {/* Key Responsibilities */}
          {job.keyResponsibilities && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Key Responsibilities</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{job.keyResponsibilities}</p>
            </div>
          )}
          {/* Key Attributes */}
          {job.keyAttributes && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Key Attributes</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{job.keyAttributes}</p>
            </div>
          )}
          {/* Experience */}
          {job.experience && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Experience</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{job.experience}</p>
            </div>
          )}
        </div>
        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">Close</button>
        </div>
      </div>
    </div>
  );
}

function JobPostingsTab({ detail, isPlatformSuperAdmin, handleResumeScoreClick }) {
  const [localJobs, setLocalJobs] = useState(detail.jobPostings || []);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jdJob, setJdJob] = useState(null);
  const [openMenuJobId, setOpenMenuJobId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [confirmDeleteJobId, setConfirmDeleteJobId] = useState(null);
  const [deletingJobId, setDeletingJobId] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    setLocalJobs(detail.jobPostings || []);
  }, [detail.jobPostings]);

  useEffect(() => {
    if (!openMenuJobId) return;
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuJobId(null);
      }
    }
    function handleScroll() {
      setOpenMenuJobId(null);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [openMenuJobId]);

  const handleDeleteConfirm = async () => {
    if (!confirmDeleteJobId) return;
    setDeletingJobId(confirmDeleteJobId);
    try {
      await axios.delete(`/api/internal/jobs/${confirmDeleteJobId}`);
      setLocalJobs((prev) => prev.filter((j) => String(j._id) !== String(confirmDeleteJobId)));
      toast.success('Job and all associated data permanently deleted');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to delete job');
    } finally {
      setDeletingJobId(null);
      setConfirmDeleteJobId(null);
    }
  };

  if (selectedJob) {
    return (
      <ScreeningsList
        jobId={selectedJob._id}
        jobTitle={selectedJob.title}
        onBack={() => setSelectedJob(null)}
        isPlatformSuperAdmin={isPlatformSuperAdmin}
        handleResumeScoreClick={handleResumeScoreClick}
      />
    );
  }

  if (!localJobs.length) return <EmptyState text="No job postings yet" />;

  const jobToDelete = localJobs.find((j) => String(j._id) === String(confirmDeleteJobId));

  return (
    <>
      {jdJob && <JDModal job={jdJob} onClose={() => setJdJob(null)} />}

      {/* Permanent Delete Confirmation Modal */}
      {confirmDeleteJobId && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-gray-900/50"
            onClick={() => !deletingJobId && setConfirmDeleteJobId(null)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6 z-10">
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-100">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-gray-900">Permanently Delete Job</h3>
                <p className="mt-1 text-sm text-gray-500">
                  You are about to permanently delete{' '}
                  <span className="font-medium text-gray-800">
                    &ldquo;{jobToDelete?.title || 'this job'}&rdquo;
                  </span>
                  . This will irreversibly delete:
                </p>
                <ul className="mt-2 text-sm text-gray-500 list-disc list-inside space-y-0.5">
                  <li>The job posting</li>
                  <li>All associated screenings &amp; candidates</li>
                  <li>All assessments &amp; AI interviews</li>
                </ul>
                <p className="mt-3 text-sm font-semibold text-red-600">
                  This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button
                onClick={() => setConfirmDeleteJobId(null)}
                disabled={!!deletingJobId}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={!!deletingJobId}
                className="px-4 py-2 rounded-lg bg-red-600 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deletingJobId === confirmDeleteJobId ? 'Deleting…' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
              <th className="pb-2 pr-4 font-medium">Title</th>
              <th className="pb-2 pr-4 font-medium">Location</th>
              <th className="pb-2 pr-4 font-medium">Type</th>
              <th className="pb-2 pr-4 font-medium">Status</th>
              <th className="pb-2 pr-4 font-medium">Posted</th>
              <th className="pb-2 pr-4 font-medium">View</th>
              <th className="pb-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {localJobs.map((job, idx) => {
              const isClosed = (job.status || '').toLowerCase() === 'closed';
              const menuOpen = openMenuJobId === String(job._id);
              return (
                <tr
                  key={job._id || idx}
                  className="border-b border-gray-50 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => setSelectedJob(job)}
                >
                  <td className="py-2.5 pr-4 text-gray-800 font-medium">
                    <div className="flex flex-col gap-0.5">
                      <span>{job.title || '—'}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); setJdJob(job); }}
                        className="text-[11px] text-indigo-600 hover:underline font-medium text-left w-fit"
                      >
                        View JD
                      </button>
                    </div>
                  </td>
                  <td className="py-2.5 pr-4 text-gray-600">{job.location || '—'}</td>
                  <td className="py-2.5 pr-4">
                    <Badge className="bg-gray-100 text-gray-600">
                      {capitalize(job.locationType || job.type || '')}
                    </Badge>
                  </td>
                  <td className="py-2.5 pr-4">
                    <Badge className={statusColor(job.status)}>{capitalize(job.status)}</Badge>
                  </td>
                  <td className="py-2.5 pr-4 text-gray-500 tabular-nums">
                    {fmtDate(job.createdAt || job.postedAt)}
                  </td>
                  <td className="py-2.5 pr-4">
                    <span className="text-xs text-[#007BFF] font-medium inline-flex items-center gap-1">
                      View Screenings <ChevronDown className="h-3 w-3 -rotate-90" />
                    </span>
                  </td>
                  <td className="py-2.5 text-right">
                    {isClosed ? (
                      <div
                        className="inline-block"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={(e) => {
                            if (menuOpen) {
                              setOpenMenuJobId(null);
                            } else {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setMenuPosition({
                                top: rect.bottom + window.scrollY + 4,
                                left: rect.right + window.scrollX - 176,
                              });
                              setOpenMenuJobId(String(job._id));
                            }
                          }}
                          className="inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                          title="Actions"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-300 select-none">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Portal-rendered dropdown — escapes overflow containers */}
      {openMenuJobId && createPortal(
        <div
          ref={menuRef}
          style={{ position: 'absolute', top: menuPosition.top, left: menuPosition.left, zIndex: 9999 }}
          className="w-44 bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden"
        >
          <button
            onClick={() => {
              setOpenMenuJobId(null);
              setConfirmDeleteJobId(openMenuJobId);
            }}
            className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2.5 transition-colors"
          >
            <Trash2 className="h-4 w-4 shrink-0" />
            <span>Permanently delete</span>
          </button>
        </div>,
        document.body
      )}
    </>
  );
}

function SupportTab({ detail, setActiveView }) {
  const requests = detail.supportRequests || [];
  if (!requests.length) return <EmptyState text="No support requests found" />;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {requests.map((req, idx) => (
          <div
            key={req._id || idx}
            className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-100 bg-white px-4 py-3 text-sm hover:bg-gray-50 transition-colors"
          >
            <span className="text-xs text-gray-400 font-mono tabular-nums shrink-0">
              #{req.ticketId || req._id?.slice(-6) || idx + 1}
            </span>
            <span className="text-gray-800 font-medium flex-1 min-w-0 truncate">{req.title || req.subject || '—'}</span>
            {req.category && <Badge className="bg-gray-100 text-gray-500">{req.category}</Badge>}
            <Badge className={priorityColor(req.priority)}>{capitalize(req.priority)}</Badge>
            <Badge className={supportStatusColor(req.status)}>{capitalize(req.status?.replace('_', ' '))}</Badge>
            <span className="text-xs text-gray-400 tabular-nums shrink-0">{fmtDate(req.createdAt)}</span>
          </div>
        ))}
      </div>
      <button
        onClick={() => setActiveView?.('support')}
        className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
      >
        <LifeBuoy className="h-4 w-4" />
        View all in support inbox
      </button>
    </div>
  );
}

// Plan default on-demand rates (₹ per unit) — V2 credit-based
const BILLING_PLAN_RATES = {
  starter: { interview: 300, assessment: 150 },
  core: { interview: 250, assessment: 125 },
  custom: { interview: 250, assessment: 125 },
  free: { interview: 300, assessment: 150 },
};
const BILLING_PLAN_CREDITS = { starter: 100, core: 250 };

function BillingTab({ detail, orgId, onPlanUpdated }) {
  const [file, setFile] = useState(null);
  const [amount, setAmount] = useState('');
  const [cycle, setCycle] = useState('monthly');
  const [invoiceNotes, setInvoiceNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [invoices, setInvoices] = useState(detail.invoices || []);
  const [editing, setEditing] = useState(false);
  const [planForm, setPlanForm] = useState({
    plan: detail.plan || 'free',
    packageId: detail.packageId || '',
    billingPeriod: detail.billingPeriod || 'monthly',
    planMonthlyPrice: detail.planMonthlyPrice || '',
    planCurrency: detail.planCurrency || 'INR',
    total_ai_credits: detail.total_ai_credits || detail.totalAiCredits || '',
    package_expiry_date: detail.package_expiry_date ? new Date(detail.package_expiry_date).toISOString().slice(0, 10) : '',
    billing_cycle_anchor: detail.billing_cycle_anchor ? new Date(detail.billing_cycle_anchor).toISOString().slice(0, 10) : '',
    billing_cycle_duration_months: detail.billing_cycle_duration_months ?? 12,
    status: detail.status || 'active',
    // V2 credits
    plan_credits: detail.plan_credits ?? '',
    plan_total_users: detail.plan_total_users ?? '',
    // V1 quotas (legacy)
    quota_video_interviews: detail.quota_video_interviews ?? '',
    quota_audio_interviews: detail.quota_audio_interviews ?? '',
    quota_assessments: detail.quota_assessments ?? '',
    quota_resume_scores: detail.quota_resume_scores ?? '',
    // PAYG
    ondemand_enabled: detail.ondemand_enabled !== false,
    ondemand_limit: detail.ondemand_limit ?? '',
    ondemand_rate_interview: detail.ondemand_rate_interview ?? '',
    ondemand_rate_video: detail.ondemand_rate_video ?? '',
    ondemand_rate_audio: detail.ondemand_rate_audio ?? '',
    ondemand_rate_assessment: detail.ondemand_rate_assessment ?? '',
    ondemand_rate_resume: detail.ondemand_rate_resume ?? '',
    // Custom credit cost overrides
    credit_cost_interview: detail.credit_cost_interview ?? '',
    credit_cost_assessment: detail.credit_cost_assessment ?? '',
    credit_cost_resume: detail.credit_cost_resume ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [packages, setPackages] = useState([]);

  // On-demand usage for current period
  const [odUsage, setOdUsage] = useState(null);
  useEffect(() => {
    if (!orgId) return;
    axios.get(`/api/internal/org/${orgId}/ondemand-usage`)
      .then(({ data }) => setOdUsage(data))
      .catch(() => setOdUsage(null));
  }, [orgId]);

  useEffect(() => {
    axios.get('/api/internal/packages')
      .then(({ data }) => setPackages(data.packages || []))
      .catch(() => setPackages([]));
  }, []);

  const handleUpload = async () => {
    if (!file) { toast.error('Please select a file first'); return; }
    if (!amount || parseFloat(amount) <= 0) { toast.error('Please enter the invoice amount'); return; }
    const formData = new FormData();
    formData.append('invoice', file);
    formData.append('amount', amount);
    formData.append('cycle', cycle);
    formData.append('notes', invoiceNotes);
    formData.append('currency', planForm.planCurrency);
    setUploading(true);
    try {
      const { data } = await axios.post(`/api/internal/subscriptions/${orgId}/invoice`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Invoice uploaded successfully');
      setFile(null); setAmount(''); setInvoiceNotes('');
      if (data.invoice) setInvoices((prev) => [data.invoice, ...prev]);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to upload invoice');
    } finally { setUploading(false); }
  };

  const handleSavePlan = async () => {
    setSaving(true);
    try {
      await axios.patch(`/api/internal/org/${orgId}/plan`, {
        ...planForm,
        packageId: planForm.packageId || null,
        planMonthlyPrice: parseFloat(planForm.planMonthlyPrice) || 0,
        total_ai_credits: parseInt(planForm.total_ai_credits) || 0,
        package_expiry_date: planForm.package_expiry_date || null,
        billing_cycle_anchor: planForm.billing_cycle_anchor || null,
        billing_cycle_duration_months: parseInt(planForm.billing_cycle_duration_months) || 12,
        // V2 credits
        plan_credits: planForm.plan_credits === '' ? null : parseInt(planForm.plan_credits) || null,
        plan_total_users: planForm.plan_total_users === '' ? null : parseInt(planForm.plan_total_users) || 0,
        // V1 legacy quotas
        quota_video_interviews: planForm.quota_video_interviews === '' ? null : parseInt(planForm.quota_video_interviews) || 0,
        quota_audio_interviews: planForm.quota_audio_interviews === '' ? null : parseInt(planForm.quota_audio_interviews) || 0,
        quota_assessments: planForm.quota_assessments === '' ? null : parseInt(planForm.quota_assessments) || 0,
        quota_resume_scores: planForm.quota_resume_scores === '' ? null : parseInt(planForm.quota_resume_scores) || 0,
        // PAYG
        ondemand_enabled: planForm.ondemand_enabled,
        ondemand_limit: planForm.ondemand_limit === '' ? null : parseFloat(planForm.ondemand_limit) || 0,
        ondemand_rate_interview: planForm.ondemand_rate_interview === '' ? null : parseFloat(planForm.ondemand_rate_interview),
        ondemand_rate_video: planForm.ondemand_rate_video === '' ? null : parseFloat(planForm.ondemand_rate_video),
        ondemand_rate_audio: planForm.ondemand_rate_audio === '' ? null : parseFloat(planForm.ondemand_rate_audio),
        ondemand_rate_assessment: planForm.ondemand_rate_assessment === '' ? null : parseFloat(planForm.ondemand_rate_assessment),
        ondemand_rate_resume: planForm.ondemand_rate_resume === '' ? null : parseFloat(planForm.ondemand_rate_resume),
        // Custom credit costs overrides
        credit_cost_interview: planForm.credit_cost_interview === '' ? null : parseFloat(planForm.credit_cost_interview),
        credit_cost_assessment: planForm.credit_cost_assessment === '' ? null : parseFloat(planForm.credit_cost_assessment),
        credit_cost_resume: planForm.credit_cost_resume === '' ? null : parseFloat(planForm.credit_cost_resume),
      });
      toast.success('Plan updated');
      setEditing(false);
      onPlanUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update plan');
    } finally { setSaving(false); }
  };

  const inputCls = 'w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20 outline-none transition';

  // Helper to render quota used/total
  const quotaLabel = (quota, used) => {
    if (quota == null) return <span className="text-gray-400">Unlimited</span>;
    const pct = quota > 0 ? Math.min(Math.round((used / quota) * 100), 100) : 0;
    const color = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500';
    return (
      <div className="space-y-1">
        <span className="font-semibold tabular-nums">{used.toLocaleString()} / {quota.toLocaleString()}</span>
        <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div className={cn('h-full rounded-full transition-all', color)} style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  };

  // Get quota usage from detail
  const qu = detail.quotaUsage || {};

  return (
    <div className="space-y-6">
      {/* Plan & Subscription card */}
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-semibold text-gray-800">Plan & Subscription</h4>
          {!editing ? (
            <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 text-xs font-medium text-[#007BFF] hover:text-blue-700 transition-colors">
              <Edit3 className="h-3.5 w-3.5" /> Edit Plan
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button onClick={() => setEditing(false)} className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700"><X className="h-3.5 w-3.5" /> Cancel</button>
              <button onClick={handleSavePlan} disabled={saving} className="inline-flex items-center gap-1 text-xs font-medium text-white bg-[#007BFF] hover:bg-blue-600 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50">
                {saving ? <Spinner className="h-3.5 w-3.5 text-white" /> : <Save className="h-3.5 w-3.5" />} Save
              </button>
            </div>
          )}
        </div>
        {!editing ? (
          <div className="space-y-4">
            {/* Plan basics */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div><p className="text-gray-500">Plan</p><p className="font-medium mt-0.5"><Badge className={planColor(detail.plan)}>{capitalize(detail.plan)}</Badge></p></div>
              <div><p className="text-gray-500">Billing</p><p className="font-medium mt-0.5">{capitalize(detail.billingPeriod)}</p></div>
              <div><p className="text-gray-500">Monthly Price</p><p className="font-medium tabular-nums mt-0.5">{detail.planCurrency === 'USD' ? fmtUSD(detail.planMonthlyPrice) : fmtINR(detail.planMonthlyPrice)}</p></div>
              <div><p className="text-gray-500">Status</p><p className="font-medium mt-0.5"><Badge className={statusColor(detail.status)}>{capitalize(detail.status)}</Badge></p></div>
              <div><p className="text-gray-500">Created</p><p className="font-medium mt-0.5">{fmtDate(detail.createdAt)}</p></div>
              {detail.package_expiry_date && <div><p className="text-gray-500">Expires</p><p className="font-medium mt-0.5">{fmtDate(detail.package_expiry_date)}</p></div>}
              {detail.billing_cycle_anchor && <div><p className="text-gray-500">Billing Cycle Start</p><p className="font-medium text-blue-600 mt-0.5">{fmtDate(detail.billing_cycle_anchor)} ({detail.billing_cycle_duration_months || 12} mo)</p></div>}
              <div><p className="text-gray-500">PAYG</p><p className="font-medium mt-0.5"><Badge className={detail.ondemand_enabled !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}>{detail.ondemand_enabled !== false ? 'Enabled' : 'Disabled'}</Badge></p></div>
              {detail.ondemand_limit != null && <div><p className="text-gray-500">PAYG Limit</p><p className="font-medium tabular-nums mt-0.5">{fmtINR(detail.ondemand_limit)}/mo</p></div>}
            </div>

            {/* Credit / quota usage */}
            {qu.version === 'v2' ? (
              <div className="pt-4 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Credit usage (current period)</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                  <div><p className="text-gray-500 text-xs mb-1">Credits</p><p className="font-semibold tabular-nums">{qu.creditsUsed ?? 0} / {qu.credits ?? '—'}</p></div>
                  <div><p className="text-gray-500 text-xs mb-1">Interviews</p><p className="font-medium tabular-nums">{qu.breakdown?.interviews?.count ?? 0}</p></div>
                  <div><p className="text-gray-500 text-xs mb-1">Assessments</p><p className="font-medium tabular-nums">{qu.breakdown?.assessments?.count ?? 0}</p></div>
                  <div><p className="text-gray-500 text-xs mb-1">Resume Scores</p><p className="font-medium tabular-nums">{qu.resumeScores ?? 0} <span className="text-gray-400 text-[10px]">(unlimited)</span></p></div>
                </div>
                {qu.teamUsers?.quota != null && (
                  <div className="mt-2 text-sm"><p className="text-gray-500 text-xs mb-1">Team Users</p><p className="font-medium tabular-nums">{qu.teamUsers.used ?? 0} / {qu.teamUsers.quota}</p></div>
                )}
                {/* Custom credit cost overrides details */}
                <div className="mt-3.5 grid grid-cols-1 sm:grid-cols-3 gap-3 border border-gray-100 bg-gray-50/50 p-3.5 rounded-xl">
                  <div>
                    <p className="text-gray-500 text-[10px] uppercase font-semibold tracking-wider mb-0.5">Interview Credit Cost</p>
                    <p className="font-semibold text-gray-800 tabular-nums text-xs">{detail.credit_cost_interview != null ? `${detail.credit_cost_interview} Cr` : '1.0 Cr (Default)'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-[10px] uppercase font-semibold tracking-wider mb-0.5">Assessment Credit Cost</p>
                    <p className="font-semibold text-gray-800 tabular-nums text-xs">{detail.credit_cost_assessment != null ? `${detail.credit_cost_assessment} Cr` : '0.5 Cr (Default)'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-[10px] uppercase font-semibold tracking-wider mb-0.5">Resume Credit Cost</p>
                    <p className="font-semibold text-gray-800 tabular-nums text-xs">{detail.credit_cost_resume != null ? `${detail.credit_cost_resume} Cr` : 'Free (Default)'}</p>
                  </div>
                </div>
              </div>
            ) : (qu.video_interviews || qu.audio_interviews || qu.assessments || qu.resume_scores) && (
              <div className="pt-4 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Feature quotas (current period)</p>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-sm">
                  {[
                    { label: 'Video Interviews', data: qu.video_interviews },
                    { label: 'Audio Interviews', data: qu.audio_interviews },
                    { label: 'Assessments', data: qu.assessments },
                    { label: 'Resume Scores', data: qu.resume_scores },
                    { label: 'Team Users', data: qu.team_users },
                  ].map((q) => q.data && (
                    <div key={q.label}>
                      <p className="text-gray-500 text-xs mb-1">{q.label}</p>
                      {quotaLabel(q.data.quota, q.data.used)}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* On-demand usage */}
            {detail.ondemand_enabled !== false && (qu.version === 'v2' || qu.video_interviews || qu.audio_interviews || qu.assessments || qu.resume_scores) && (() => {
              let quotaItems, totalOdCount, totalOdSpend;
              if (qu.version === 'v2') {
                const rates = qu.onDemandRates || BILLING_PLAN_RATES[(detail.plan || 'starter').toLowerCase()];
                quotaItems = [
                  { key: 'interview', label: 'Interviews', rate: rates?.interview },
                  { key: 'assessment', label: 'Assessments', rate: rates?.assessment },
                ].filter((qi) => qi.rate != null);
                totalOdSpend = qu.onDemand?.totalSpend ?? 0;
                totalOdCount = (qu.onDemand?.interview?.count ?? 0) + (qu.onDemand?.assessment?.count ?? 0);
              } else {
                quotaItems = [
                  { key: 'video_interviews', label: 'Video Interviews', rate: BILLING_PLAN_RATES[(detail.plan || 'starter').toLowerCase()]?.interview },
                  { key: 'audio_interviews', label: 'Audio Interviews', rate: BILLING_PLAN_RATES[(detail.plan || 'starter').toLowerCase()]?.interview },
                  { key: 'assessments', label: 'Assessments', rate: BILLING_PLAN_RATES[(detail.plan || 'starter').toLowerCase()]?.assessment },
                ].filter((qi) => qi.rate != null);
                totalOdCount = quotaItems.reduce((s, qi) => s + (qu[qi.key]?.onDemand?.count ?? 0), 0);
                totalOdSpend = quotaItems.reduce((s, qi) => s + (qu[qi.key]?.onDemand?.totalSpend ?? 0), 0);
              }
              return (
                <div className="pt-4 border-t border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">On-demand usage (current period)</p>
                  <div className="rounded-lg border border-gray-100 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50/80 border-b border-gray-100">
                          <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Type</th>
                          <th className="px-4 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Qty</th>
                          <th className="px-4 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Rate</th>
                          <th className="px-4 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {quotaItems.map((qi) => {
                          const od = qu.version === 'v2'
                            ? (qu.onDemand?.[qi.key] || { count: 0, totalSpend: 0 })
                            : (qu[qi.key]?.onDemand || { count: 0, totalSpend: 0 });
                          const qty = od.count ?? 0;
                          const total = od.totalSpend ?? 0;
                          const rate = qty > 0 ? total / qty : (qi.rate ?? 0);
                          return (
                            <tr key={qi.key} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                              <td className="px-4 py-2.5 font-medium text-gray-800">{qi.label}</td>
                              <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">{qty.toLocaleString()}</td>
                              <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">{fmtINR(rate)}/unit</td>
                              <td className="px-4 py-2.5 text-right tabular-nums font-medium text-gray-800">{fmtINR(total)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="bg-gray-50/50 border-t border-gray-100">
                          <td className="px-4 py-2.5 font-semibold text-gray-800" colSpan={3}>Subtotal</td>
                          <td className="px-4 py-2.5 text-right font-bold tabular-nums text-gray-800">{fmtINR(totalOdSpend)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  {totalOdCount === 0 && (
                    <p className="mt-3 text-xs text-gray-500">No on-demand usage this period.</p>
                  )}
                </div>
              );
            })()}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div><label className="block text-xs text-gray-500 mb-1">Package</label>
                <select value={planForm.packageId ? String(planForm.packageId) : ''} onChange={(e) => setPlanForm((p) => ({ ...p, packageId: e.target.value || null }))} className={inputCls}>
                  <option value="">None (use plan)</option>
                  {(planForm._packages || []).map((p) => (
                    <option key={p._id} value={p._id}>{p.displayName || p.name} ({p.slug})</option>
                  ))}
                </select></div>
              <div><label className="block text-xs text-gray-500 mb-1">Plan</label>
                <select value={planForm.plan} onChange={(e) => setPlanForm((p) => ({ ...p, plan: e.target.value }))} className={inputCls}>
                  <option value="free">Free</option><option value="starter">Starter</option><option value="core">Core</option><option value="custom">Custom</option>
                </select></div>
              <div><label className="block text-xs text-gray-500 mb-1">Billing Period</label>
                <select value={planForm.billingPeriod} onChange={(e) => setPlanForm((p) => ({ ...p, billingPeriod: e.target.value }))} className={inputCls}>
                  <option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="yearly">Yearly</option>
                </select></div>
              <div><label className="block text-xs text-gray-500 mb-1">Monthly Price</label>
                <input type="number" min="0" value={planForm.planMonthlyPrice} onChange={(e) => setPlanForm((p) => ({ ...p, planMonthlyPrice: e.target.value }))} className={inputCls} placeholder="0" /></div>
              <div><label className="block text-xs text-gray-500 mb-1">Currency</label>
                <select value={planForm.planCurrency} onChange={(e) => setPlanForm((p) => ({ ...p, planCurrency: e.target.value }))} className={inputCls}>
                  <option value="INR">INR</option><option value="USD">USD</option>
                </select></div>
              <div><label className="block text-xs text-gray-500 mb-1">Expiry Date</label>
                <input type="date" value={planForm.package_expiry_date} onChange={(e) => setPlanForm((p) => ({ ...p, package_expiry_date: e.target.value }))} className={inputCls} /></div>
              <div><label className="block text-xs text-gray-500 mb-1 font-semibold text-blue-600">Billing Cycle Start</label>
                <input type="date" value={planForm.billing_cycle_anchor} onChange={(e) => setPlanForm((p) => ({ ...p, billing_cycle_anchor: e.target.value }))} className={inputCls} title="Start date of paid billing cycle (resets usage calculations from this date)" /></div>
              <div><label className="block text-xs text-gray-500 mb-1">Cycle Duration (mo)</label>
                <input type="number" min="1" max="60" value={planForm.billing_cycle_duration_months} onChange={(e) => setPlanForm((p) => ({ ...p, billing_cycle_duration_months: e.target.value }))} className={inputCls} placeholder="12" /></div>
              <div><label className="block text-xs text-gray-500 mb-1">Status</label>
                <select value={planForm.status} onChange={(e) => setPlanForm((p) => ({ ...p, status: e.target.value }))} className={inputCls}>
                  <option value="active">Active</option><option value="inactive">Inactive</option><option value="suspended">Suspended</option>
                </select></div>
            </div>

            {/* V2 Credits */}
            <div className="pt-3 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Credits (V2)</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div><label className="block text-[11px] text-gray-500 mb-1">Plan Credits</label>
                  <input type="number" min="0" value={planForm.plan_credits} onChange={(e) => setPlanForm((p) => ({ ...p, plan_credits: e.target.value }))} className={inputCls} placeholder={BILLING_PLAN_CREDITS[planForm.plan] ? String(BILLING_PLAN_CREDITS[planForm.plan]) : 'null'} /></div>
                <div><label className="block text-[11px] text-gray-500 mb-1">Team Users</label>
                  <input type="number" min="0" value={planForm.plan_total_users} onChange={(e) => setPlanForm((p) => ({ ...p, plan_total_users: e.target.value }))} className={inputCls} placeholder="null" /></div>
                <div className="flex items-end text-[10px] text-gray-500 pb-2">1 interview = 1 credit · 1 assessment = 0.5 credit · resumes = unlimited</div>
              </div>
            </div>

            {/* Custom Credit Cost Overrides (V2) */}
            <div className="pt-3 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Custom Credit Costs (Overrides)</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[11px] text-gray-500 mb-1">AI Interview Cost (Cr)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={planForm.credit_cost_interview}
                    onChange={(e) => setPlanForm((p) => ({ ...p, credit_cost_interview: e.target.value }))}
                    className={inputCls}
                    placeholder="Plan Default (1.0)"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-500 mb-1">Custom Assessment Cost (Cr)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={planForm.credit_cost_assessment}
                    onChange={(e) => setPlanForm((p) => ({ ...p, credit_cost_assessment: e.target.value }))}
                    className={inputCls}
                    placeholder="Plan Default (0.5)"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-500 mb-1">Resume Screening Cost (Cr)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={planForm.credit_cost_resume}
                    onChange={(e) => setPlanForm((p) => ({ ...p, credit_cost_resume: e.target.value }))}
                    className={inputCls}
                    placeholder="Plan Default (0.0 / Free)"
                  />
                </div>
              </div>
            </div>

            {/* V1 Quotas (legacy) */}
            <div className="pt-3 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Feature quotas (legacy V1)</p>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <div><label className="block text-[11px] text-gray-500 mb-1">Video Interviews</label>
                  <input type="number" min="0" value={planForm.quota_video_interviews} onChange={(e) => setPlanForm((p) => ({ ...p, quota_video_interviews: e.target.value }))} className={inputCls} placeholder="null" /></div>
                <div><label className="block text-[11px] text-gray-500 mb-1">Audio Interviews</label>
                  <input type="number" min="0" value={planForm.quota_audio_interviews} onChange={(e) => setPlanForm((p) => ({ ...p, quota_audio_interviews: e.target.value }))} className={inputCls} placeholder="null" /></div>
                <div><label className="block text-[11px] text-gray-500 mb-1">Assessments</label>
                  <input type="number" min="0" value={planForm.quota_assessments} onChange={(e) => setPlanForm((p) => ({ ...p, quota_assessments: e.target.value }))} className={inputCls} placeholder="null" /></div>
                <div><label className="block text-[11px] text-gray-500 mb-1">Resume Scores</label>
                  <input type="number" min="0" value={planForm.quota_resume_scores} onChange={(e) => setPlanForm((p) => ({ ...p, quota_resume_scores: e.target.value }))} className={inputCls} placeholder="null" /></div>
                <div><label className="block text-[11px] text-gray-500 mb-1">Team Users</label>
                  <input type="number" min="0" value={planForm.plan_total_users} onChange={(e) => setPlanForm((p) => ({ ...p, plan_total_users: e.target.value }))} className={inputCls} placeholder="null" /></div>
              </div>
            </div>

            {/* PAYG settings */}
            <div className="pt-3 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">PAYG settings</p>
              <div className="flex flex-wrap items-center gap-4 mb-3">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={planForm.ondemand_enabled} onChange={(e) => setPlanForm((p) => ({ ...p, ondemand_enabled: e.target.checked }))}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer" />
                  <span className="text-sm text-gray-700">Enable PAYG</span>
                </label>
                <div className="w-44">
                  <label className="block text-[11px] text-gray-500 mb-1">Spend limit (₹/mo)</label>
                  <input type="number" min="0" value={planForm.ondemand_limit} onChange={(e) => setPlanForm((p) => ({ ...p, ondemand_limit: e.target.value }))}
                    className={inputCls} placeholder="No limit" disabled={!planForm.ondemand_enabled} />
                </div>
              </div>
              {planForm.ondemand_enabled && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {(() => {
                    const rates = BILLING_PLAN_RATES[planForm.plan] || BILLING_PLAN_RATES.starter;
                    return (
                      <>
                        <div><label className="block text-[11px] text-gray-500 mb-1">Interview rate (₹)</label>
                          <input type="number" min="0" step="0.01" value={planForm.ondemand_rate_interview || rates.interview} className={cn(inputCls, 'bg-gray-100 cursor-not-allowed')} disabled readOnly />
                          <p className="text-[10px] text-gray-400 mt-0.5">Plan default: ₹{rates.interview}</p></div>
                        <div><label className="block text-[11px] text-gray-500 mb-1">Assessment rate (₹)</label>
                          <input type="number" min="0" step="0.01" value={planForm.ondemand_rate_assessment || rates.assessment} className={cn(inputCls, 'bg-gray-100 cursor-not-allowed')} disabled readOnly />
                          <p className="text-[10px] text-gray-400 mt-0.5">Plan default: ₹{rates.assessment}</p></div>
                        <div className="col-span-2 flex items-end text-[10px] text-gray-500 italic">
                          V2: Unified interview rate (video + audio)
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* On-demand Usage card (current period) */}
      {odUsage && odUsage.totalCount > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <h4 className="text-sm font-semibold text-gray-800 mb-3">On-demand Usage (current period)</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm mb-3">
            {['video_interview', 'audio_interview', 'assessment', 'resume_score'].map((type) => {
              const data = odUsage.byType?.[type];
              if (!data) return null;
              const labels = { video_interview: 'Video', audio_interview: 'Audio', assessment: 'Assessment', resume_score: 'Resume' };
              return (
                <div key={type} className="rounded-lg bg-gray-50 border border-gray-100 p-3">
                  <p className="text-xs text-gray-500 mb-1">{labels[type]}</p>
                  <p className="text-lg font-bold text-gray-900 tabular-nums">{data.count}</p>
                  <p className="text-xs text-gray-500 tabular-nums">{fmtINR(data.totalSpend)}</p>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-gray-500">Total PAYG spend:</span>
            <span className="font-bold text-gray-900 tabular-nums">{fmtINR(odUsage.totalSpend)}</span>
            {odUsage.ondemand_limit != null && (
              <span className="text-xs text-gray-400">/ {fmtINR(odUsage.ondemand_limit)} limit</span>
            )}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <h4 className="text-sm font-semibold text-gray-800 mb-3">Upload Invoice</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Amount *</label>
            <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} placeholder="e.g. 15000" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Billing Cycle</label>
            <select value={cycle} onChange={(e) => setCycle(e.target.value)} className={inputCls}>
              <option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="yearly">Yearly</option><option value="one-time">One-time</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs text-gray-500 mb-1">Notes (optional)</label>
            <input type="text" value={invoiceNotes} onChange={(e) => setInvoiceNotes(e.target.value)} className={inputCls} placeholder="e.g. March 2026 billing" />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
          <div className="flex-1 w-full">
            <label className="block text-xs text-gray-500 mb-1">PDF or image file *</label>
            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-[#007BFF] file:px-4 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-blue-600 file:cursor-pointer file:transition-colors" />
          </div>
          <button onClick={handleUpload} disabled={uploading || !file || !amount}
            className={cn('inline-flex items-center gap-2 rounded-lg px-5 py-2 text-sm font-medium text-white transition-colors shrink-0', uploading || !file || !amount ? 'bg-gray-300 cursor-not-allowed' : 'bg-[#007BFF] hover:bg-blue-600')}>
            {uploading ? <Spinner className="h-4 w-4 text-white" /> : <Upload className="h-4 w-4" />}
            {uploading ? 'Uploading…' : 'Upload Invoice'}
          </button>
        </div>
      </div>

      {invoices.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <h4 className="text-sm font-semibold text-gray-800 mb-3">Invoice History</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                <th className="pb-2 pr-4 font-medium">Date</th>
                <th className="pb-2 pr-4 font-medium">Amount</th>
                <th className="pb-2 pr-4 font-medium">Cycle</th>
                <th className="pb-2 pr-4 font-medium">Notes</th>
                <th className="pb-2 font-medium">File</th>
              </tr></thead>
              <tbody>
                {invoices.map((inv, i) => (
                  <tr key={inv._id || i} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-2.5 pr-4 text-gray-600 tabular-nums">{fmtDate(inv.createdAt || inv.uploadedAt)}</td>
                    <td className="py-2.5 pr-4 font-medium tabular-nums">{(inv.currency || 'INR') === 'USD' ? fmtUSD(inv.amount) : fmtINR(inv.amount)}</td>
                    <td className="py-2.5 pr-4 text-gray-600">{capitalize(inv.cycle)}</td>
                    <td className="py-2.5 pr-4 text-gray-500 max-w-[200px] truncate">{inv.notes || '—'}</td>
                    <td className="py-2.5">
                      <a href={inv.fileUrl || inv.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#007BFF] hover:underline">
                        <ExternalLink className="h-3.5 w-3.5" /> View
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── client configurations tab ──────────────────────────────────────────── */

// Custom features that can be toggled per org (not standard platform features)
const CUSTOM_FEATURES = [
  {
    key: 'passage_reading',
    label: 'Pronunciation',
    desc: 'Enables the Pronunciation assessment type under Advanced Assessments. When disabled, clients cannot create or access Pronunciation assessments.',
    defaultEnabled: false,
  },
  {
    key: 'paragraph_reading',
    label: 'Paragraph Reading',
    desc: 'Enables the Paragraph Reading / Grasping Test under Advanced Assessments. When disabled, clients cannot create or access Paragraph Reading assessments.',
    defaultEnabled: false,
  },
  {
    key: 'multi_module_assessment',
    label: 'Multi-Module Assessment',
    desc: 'Combine multiple assessments into a sequential multi-module test with independent timers and scoring.',
    defaultEnabled: false,
  },
  {
    key: 'multi_module_table_scores',
    label: 'Multi-Module Table Scores',
    desc: 'Shows a separate score column for each assessment module in the candidate table (percentage and raw score). Requires Multi-Module Assessment.',
    defaultEnabled: false,
    requiresFeature: 'multi_module_assessment',
  },
  {
    key: 'personality_assessment',
    label: 'Personality Profile (Big Five)',
    desc: 'The IPIP-50 Work Style Questionnaire as an advanced assessment type and a built-in library test (usable as a multi-module module). Gives a five-trait profile; never gates or ranks candidates.',
    defaultEnabled: true,
  },
  {
    key: 'assessment_video_proctoring',
    label: 'Assessment Video Proctoring',
    desc: 'Enables real-time webcam video proctoring in assessments. When disabled, clients see a locked card in the proctoring configuration with a contact sales option.',
    defaultEnabled: false,
  },
  {
    key: 'sourcing',
    label: 'AI Candidate Sourcing',
    desc: 'Enables the AI Candidate Sourcing tab capabilities. When disabled, the tab remains visible but shows an elegant, detailed contact-sales placeholder page.',
    defaultEnabled: false,
  },
  {
    key: 'duplicate_candidate_alerts',
    label: 'Duplicate Candidate Alerts',
    desc: 'Highlights candidates who have reapplied to other screenings so recruiters can see their history without extra lookups.',
    defaultEnabled: false,
  },
  {
    key: 'email_integration',
    label: 'Custom Email Integration',
    desc: 'Allows clients to connect their own Gmail/Google Workspace accounts to send candidate emails.',
    defaultEnabled: false,
  },
  {
    key: 'reports',
    label: 'Reports — GoColours',
    desc: 'Shows the GoColours-specific Reports item in the recruiter sidebar with store, ARM, and region breakdowns.',
    defaultEnabled: false,
  },
  {
    key: 'reports_mr',
    label: 'Reports — Ajanta Pharma MR',
    desc: 'Shows the MR Reports item in the recruiter sidebar, with a breakdown per org application question (Division, HQ, Recruitment Drive Location) plus experience, education, and current-company breakdowns.',
    defaultEnabled: false,
  },
  {
    key: 'application_form',
    label: 'Application Form',
    desc: 'Adds an "Application Form" step to new screenings: after resume and documents, candidates fill the pre-interview application form (personal, family, education, experience, remuneration), prefilled from their resume. Recruiters view it and download it as a PDF in the paper format.',
    defaultEnabled: false,
  },
  {
    key: 'ai_interview_avatar',
    label: 'AI Interviewer Avatar',
    desc: 'Shows a formally dressed interviewer on the AI interview page in place of the animated circle: a male interviewer for male voices, a female interviewer for female voices. It plays on the candidate\'s device (no extra cost) and applies to every AI interview of this organisation, including ones already sent.',
    defaultEnabled: false,
  },
  {
    key: 'ai_interview_readiness_check',
    label: 'AI Interview Readiness Check',
    desc: 'Start Interview opens a check pop-up first. Voice: the candidate reads a sentence aloud and passes only when they speak loudly enough. Video interviews also get an appearance check: a vision model looks at one camera photo and asks the candidate to fix framing (face visible and roughly centred, not too far or close), lighting, or clothing (no bare chest or vest/banyan). About $0.0003 per photo. If the AI check fails or errors, the candidate is never blocked.',
    defaultEnabled: false,
  },
  {
    key: 'training_agent',
    label: 'Training Agent',
    desc: 'Groups the dashboard sidebar into two dropdowns: Hiring Agent (Job Posts, Resources, Calendar, Reports, Teams, Library) and Training Agent (Courses, Create with AI, Assign, Analytics for story-style store-team training, still being built). Support and Your Account stay at the top level.',
    defaultEnabled: false,
  },
  {
    key: 'karan_agent',
    label: 'Karan (AI Recruiting Agent)',
    desc: 'Adds a Karan dropdown to the dashboard sidebar: a chat where the company pastes a JD, Karan interviews the hiring manager (open questions, one at a time) and writes a hiring brief, optionally names companies to recruit from, says yes/no to 4 quick picks, then reviews anonymous candidates five at a time (called first) and schedules the ones it likes, which reveals their details. Free for companies; 5% of first-year CTC on a hire. Pilot, internal orgs only. Uses sample candidates and simulated calls unless the server sets KARAN_SOURCING_MODE=apollo (spends Apollo credits).',
    defaultEnabled: false,
  },
  {
    key: 'reports_bk',
    label: 'Reports — Burger King',
    desc: 'Shows the Reports item in the recruiter sidebar for organisation admins only, with a Region breakdown (from the recruiting team that owns each screening) plus a breakdown per org application question (Current City, Preferred City) and experience, education, and current-company breakdowns.',
    defaultEnabled: false,
  },
  {
    key: 'reports_bk_regional',
    label: 'Reports — Burger King, regional access',
    desc: 'Requires "Reports — Burger King". Also gives Team Admins and team users the Reports page, showing only the regions (teams) they belong to. Users in no team still get no Reports item. Off = organisation admins only.',
    defaultEnabled: false,
  },
  {
    key: 'weekly_email_report',
    label: 'Weekly Email Report',
    desc: "Automated weekly PDF report emailed to this organisation's admin users (runs Monday 12:00 IST, production only). One PDF attachment — the Excel workbook is built but no longer sent. Also requires a matching client entry in the background job registry.",
    defaultEnabled: false,
  },
  {
    key: 'monthly_email_report',
    label: 'Monthly Email Report',
    desc: "Automated monthly PDF report emailed to this organisation's admin users (runs the 1st at 12:00 IST, production only). One PDF attachment — the Excel workbook is built but no longer sent. Also requires a matching client entry in the background job registry.",
    defaultEnabled: false,
  },
  {
    key: 'recommended_chip',
    label: 'Recommended Candidate Chip',
    desc: 'Shows a "Recommended" badge on candidates who score at or above the configured threshold.',
    defaultEnabled: false,
    threshold: {
      limitKey: 'recommended_chip_threshold',
      label: 'Score Threshold (%)',
      platformDefault: 85,
      min: 1,
      max: 100,
    },
  },
  {
    key: 'store_list',
    label: 'Store List',
    desc: 'Enables custom Store List module and sidebar link for this organisation.',
    defaultEnabled: false,
  },
  {
    key: 'city_list',
    label: 'City Code Selection',
    desc: 'Enables the City Code selection field for candidates during screening applications and shows the City Codes library in the recruiter dashboard.',
    defaultEnabled: false,
  },
  {
    key: 'rejection_reason_required',
    label: 'Mandatory Rejection Reason',
    desc: 'Requires recruiters to enter a reason before rejecting a candidate. The reason is saved as a candidate note attributed to the person who rejected them, and the Notes column is shown by default on the Rejected tab.',
    defaultEnabled: false,
  },
  {
    key: 'store_decision_emails',
    label: 'Store Hiring Decision Emails',
    desc: 'Notifies the store ARM of a candidate\'s Selected/Rejected decision from the region\'s HRBP mailbox, with the EBO HR lead in CC. Requires Custom Email Integration + Store List.',
    defaultEnabled: false,
  },
  {
    key: 'arm_salary_details',
    label: 'Regional Head Salary Details Workflow',
    desc: 'Automatically sends an email link to the Regional Head (HRBP) upon candidate selection to input Designation, Date of Joining, and Gross Salary.',
    defaultEnabled: false,
  },
  {
    key: 'bk_decision_emails',
    label: 'Burger King Selection Emails',
    desc: "Burger King only. Moving a candidate to Selected opens an editable email addressed to the candidate, with the HRBP for the job post's region (Team) prefilled in CC. Does not require Store List.",
    defaultEnabled: false,
  },
  {
    key: 'bk_undecided_digest',
    label: 'Burger King Undecided Digest',
    desc: 'Burger King only. One email every day at 12:00 PM IST to central HR (Mansi, Ankit, Abhishek) with the count of undecided candidates per region — every candidate still in the Candidates tab (not Selected, Rejected or Dropped) who applied at least the chosen number of days ago. No CC; the HRBPs do not receive it. Candidates on a team that is not one of the nine regions are not counted.',
    defaultEnabled: false,
    threshold: {
      limitKey: 'bk_undecided_digest_days',
      label: 'Count candidates undecided for',
      platformDefault: 2,
      // Must match DAY_OPTIONS in server/jobs/bkUndecidedReminderJob.js — the job ignores anything else.
      options: [
        { value: 1, label: '1 day (24 hours)' },
        { value: 2, label: '2 days (48 hours)' },
        { value: 5, label: '5 days (120 hours)' },
      ],
    },
  },
  {
    key: 'bk_undecided_reminder',
    label: 'Burger King HRBP Undecided Reminder',
    desc: "Burger King only. One email per candidate, to the HRBP of the candidate's region (no CC), once the candidate has been in the Candidates tab for the chosen number of days without being moved to Selected, Rejected or Dropped. Sent once per candidate, checked every 6 hours. Skips candidates a hiring manager already decided from the share link, candidates on a team that is not one of the nine regions, and anyone who applied more than 7 days ago.",
    defaultEnabled: false,
    threshold: {
      limitKey: 'bk_undecided_reminder_days',
      label: 'Remind after candidate undecided for',
      platformDefault: 2,
      // Must match DAY_OPTIONS in server/jobs/bkHrbpUndecidedReminderJob.js — the job ignores anything else.
      options: [
        { value: 1, label: '1 day (24 hours)' },
        { value: 2, label: '2 days (48 hours)' },
        { value: 5, label: '5 days (120 hours)' },
      ],
    },
  },
  {
    key: 'gc_arm_salary_pending_digest',
    label: 'Go Colors Pending Salary Digest',
    desc: 'Go Colors only. One email every day at 12:00 PM IST to the HRBPs (Lubdha — North & West, Madhulika — South, Pooja — East), with the EBO HR lead in CC, counting per ARM the selected candidates whose ARM has not yet submitted Date of Joining and Gross Salary. Counts salary requests sent to an ARM at least the chosen number of days ago and still unfilled (expired links included), for candidates still in the Selected tab. ARMs do not receive it. No email on a day with nothing pending.',
    defaultEnabled: false,
    threshold: {
      limitKey: 'gc_arm_salary_pending_digest_days',
      label: 'Count requests unfilled for',
      platformDefault: 2,
      // Must match DAY_OPTIONS in server/jobs/gcArmSalaryPendingDigestJob.js — the job ignores anything else.
      options: [
        { value: 1, label: '1 day (24 hours)' },
        { value: 2, label: '2 days (48 hours)' },
        { value: 5, label: '5 days (120 hours)' },
      ],
    },
  },
  {
    key: 'gc_arm_salary_reminder',
    label: 'Go Colors ARM Salary Reminders',
    desc: 'Go Colors only. While a selected candidate\'s Date of Joining and Gross Salary are unfilled, the ARM (whoever the salary request went to) gets a reminder every 6 hours, up to 3, with the region HRBP in CC. Stops as soon as the form is submitted or the candidate leaves Selected. Sent between 9:00 AM and 9:00 PM IST only. Only salary requests emailed after this feature shipped are reminded.',
    defaultEnabled: false,
  },
  {
    key: 'document_verification',
    label: 'AI Document Verification (OCR)',
    desc: 'Enables in-house AI OCR and instant authenticity validation for Govt IDs (Aadhaar, PAN, Passport, DL) during candidate document collection. When disabled, recruiters see a locked enterprise card with a contact sales option.',
    defaultEnabled: false,
  },
  {
    key: 'whatsapp_agent',
    label: 'WhatsApp Agent Screening',
    desc: 'Enables the WhatsApp Agent button on the Job Screenings page to create conversational AI screening rounds over WhatsApp.',
    defaultEnabled: false,
  },
  {
    key: 'qualifying_agent',
    label: 'Qualifying Agent',
    desc: 'Enables the Qualifying Agent button on the Job Screenings page to pre-screen candidates over a phone call (salary, willingness, notice period, AI interview consent), automatically falling back to WhatsApp if the call is not picked up or drops mid-way. Requires Plivo to be configured on the server.',
    defaultEnabled: false,
  },
  {
    key: 'transcripts_confirmation',
    label: 'Transcripts Confirmation',
    desc: 'Adds an optional "Are transcripts available for each semester/year?" checkbox to the candidate resume upload step (ticked = Yes, unticked = No). Recruiters see the answer in a "Transcripts Available" column in the candidates table and Excel export, on screenings that include resume upload. Candidates can continue either way.',
    defaultEnabled: false,
  },
  {
    key: 'ajanta_consent_notice',
    label: 'Ajanta Consent Notice (DPDP)',
    desc: "Ajanta Pharma only. Shows Ajanta's own DPDP Act consent notice as a pop-up when a candidate opens an apply link, in the language the candidate picks, with an 18+ confirmation. Candidates cannot apply without agreeing; each agreement is recorded with the notice version and emailed to the candidate. The text lives in code (server/modules/consent-notice/clients/ajantaPharma.js) — nothing else to set up.",
    defaultEnabled: false,
  },
];

// Human-readable labels for each model slot
const MODEL_SLOT_LABELS = {

  resume_parsing: { label: 'Resume Parsing', desc: 'Extracts structured data from uploaded resumes' },
  resume_scoring: { label: 'Resume Scoring', desc: 'Scores resumes against job description criteria' },
  proctoring_summary: { label: 'Proctoring Summary', desc: 'AI summary of proctoring events' },
  video_proctoring: { label: 'Video Proctoring', desc: 'Analyses video frames for proctoring' },
  short_answer_grading: { label: 'Short Answer Grading', desc: 'AI grading of short-answer responses' },
  passage_reading_scoring: { label: 'Pronunciation Scoring', desc: 'Scores pronunciation assessments' },
  question_generation: { label: 'Question Generation', desc: 'AI-generated assessment questions' },
  test_suggestion: { label: 'Test Suggestion', desc: 'Suggests tests for a job description' },
  structured_search: { label: 'Structured Search', desc: 'AI-powered candidate search queries' },
  ocr: { label: 'OCR', desc: 'Optical character recognition for documents' },
  file_parsing: { label: 'File Parsing', desc: 'Parses uploaded files for content extraction' },
};

// Cost tier badge styling
const TIER_BADGE = {
  cheap: { label: 'Budget', className: 'bg-emerald-100 text-emerald-700' },
  mid: { label: 'Standard', className: 'bg-blue-100 text-blue-700' },
  expensive: { label: 'Premium', className: 'bg-amber-100 text-amber-700' },
};

// Group model slots by category for cleaner UI
const MODEL_SLOT_GROUPS = [

  { title: 'Resume Processing', icon: FileText, slots: ['resume_parsing', 'resume_scoring'] },
  { title: 'Proctoring', icon: Eye, slots: ['proctoring_summary', 'video_proctoring'] },
  { title: 'Assessment', icon: FileCheck, slots: ['short_answer_grading', 'passage_reading_scoring', 'question_generation', 'test_suggestion'] },
  { title: 'Utilities', icon: Layers, slots: ['structured_search', 'ocr', 'file_parsing'] },
];

const ClientConfigTab = ({ orgId, orgName }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [availableModels, setAvailableModels] = useState([]); // model objects with cost
  const [modelAllowlist, setModelAllowlist] = useState({}); // slot -> allowed model ids
  const [modelOverrides, setModelOverrides] = useState({}); // local edits: slot -> model id or ''
  const [featureOverrides, setFeatureOverrides] = useState({}); // feature key -> boolean
  const [limitOverrides, setLimitOverrides] = useState({}); // limit key -> value
  const [thresholdOverrides, setThresholdOverrides] = useState({}); // limitKey -> string value
  const [savingThreshold, setSavingThreshold] = useState(false);
  const [savingFeatures, setSavingFeatures] = useState(false);
  const [savingLimits, setSavingLimits] = useState(false);
  const [activeSection, setActiveSection] = useState('features'); // features | models | limits
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);

  const [showWeightsModal, setShowWeightsModal] = useState(false);
  const [savingWeights, setSavingWeights] = useState(false);
  const [weightsConfigData, setWeightsConfigData] = useState({
    all_three: { resume: '', assessment: '', interview: '' },
    res_assess: { resume: '', assessment: '' },
    res_interview: { resume: '', interview: '' },
    assess_interview: { assessment: '', interview: '' },
  });

  useEffect(() => {
    loadAll();
  }, [orgId]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [configRes, rawConfigRes, modelsRes, templatesRes] = await Promise.allSettled([
        axios.get(`/api/internal/org-config/${orgId}`),
        axios.get(`/api/internal/org-config/${orgId}/raw`),
        axios.get('/api/internal/available-models'),
        axios.get('/api/internal/config-templates'),
      ]);

      if (configRes.status === 'fulfilled') {
        setConfig(configRes.value.data?.config || null);
      }

      // Use the RAW config (overrides only) to initialise model overrides.
      // The effective (merged) config would fill in platform defaults for null
      // values, making them appear as custom overrides when they're not.
      if (rawConfigRes.status === 'fulfilled') {
        const rawModels = rawConfigRes.value.data?.config?.models || {};
        const mOverrides = {};
        Object.keys(MODEL_SLOT_LABELS).forEach((slot) => {
          mOverrides[slot] = rawModels[slot] || ''; // '' means "use platform default"
        });
        setModelOverrides(mOverrides);

        const rawLimits = rawConfigRes.value.data?.config?.limits || {};
        setLimitOverrides({
          max_questions_per_interview: rawLimits.max_questions_per_interview || '',
          max_questions_per_assessment: rawLimits.max_questions_per_assessment || '',
          proctoring_strictness: rawLimits.proctoring_strictness != null ? rawLimits.proctoring_strictness : 3,
        });
        // Load threshold overrides for features that have a threshold config
        const tOverrides = {};
        CUSTOM_FEATURES.forEach(({ threshold }) => {
          if (threshold) {
            tOverrides[threshold.limitKey] = rawLimits[threshold.limitKey] != null
              ? String(rawLimits[threshold.limitKey])
              : String(threshold.platformDefault);
          }
        });
        setThresholdOverrides(tOverrides);
      } else {
        // Fallback: no raw config available (new org) — all defaults
        const mOverrides = {};
        Object.keys(MODEL_SLOT_LABELS).forEach((slot) => { mOverrides[slot] = ''; });
        setModelOverrides(mOverrides);
        setLimitOverrides({
          max_questions_per_interview: '',
          max_questions_per_assessment: '',
          proctoring_strictness: 3,
        });
        const tOverrides = {};
        CUSTOM_FEATURES.forEach(({ threshold }) => {
          if (threshold) tOverrides[threshold.limitKey] = String(threshold.platformDefault);
        });
        setThresholdOverrides(tOverrides);
      }

      if (modelsRes.status === 'fulfilled') {
        setAvailableModels(modelsRes.value.data?.models || []);
        setModelAllowlist(modelsRes.value.data?.allowlist || {});
      }

      // Initialise feature overrides from saved config
      if (configRes.status === 'fulfilled') {
        const savedConfig = configRes.value.data?.config;
        const savedFeatures = savedConfig?.features || {};
        const fOverrides = {};
        CUSTOM_FEATURES.forEach(({ key, defaultEnabled }) => {
          fOverrides[key] = savedFeatures[key] != null ? Boolean(savedFeatures[key]) : defaultEnabled;
        });
        setFeatureOverrides(fOverrides);

        const savedWeights = savedConfig?.scoreWeights || null;
        if (savedWeights) {
          setWeightsConfigData(savedWeights);
        } else {
          setWeightsConfigData({
            all_three: { resume: '', assessment: '', interview: '' },
            res_assess: { resume: '', assessment: '' },
            res_interview: { resume: '', interview: '' },
            assess_interview: { assessment: '', interview: '' },
          });
        }
      }

      if (templatesRes.status === 'fulfilled') {
        setTemplates(templatesRes.value.data?.templates || []);
      }
    } catch (err) {
      console.error('Failed to load config data:', err);
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  // Build a lookup map: model id -> model object
  const modelLookup = useMemo(() => {
    const map = {};
    availableModels.forEach((m) => { map[m.id] = m; });
    return map;
  }, [availableModels]);

  // Platform defaults from orgDefaults.js (mirrored client-side for display)
  const PLATFORM_DEFAULTS = useMemo(() => ({

    resume_parsing: 'gpt-4o-mini',
    resume_scoring: 'gpt-4o',
    proctoring_summary: 'gpt-4o-mini',
    video_proctoring: 'gpt-4o-mini',
    short_answer_grading: 'gpt-4.1-mini',
    passage_reading_scoring: 'gpt-4.1-mini',
    question_generation: 'gpt-5-mini-2025-08-07',
    test_suggestion: 'gpt-5-mini-2025-08-07',
    structured_search: 'gpt-6-luna',
    ocr: 'gpt-4o',
    file_parsing: 'gpt-4o-mini',
  }), []);

  const handleModelChange = (slot, value) => {
    setModelOverrides((prev) => ({ ...prev, [slot]: value }));
  };

  const handleResetSlot = (slot) => {
    setModelOverrides((prev) => ({ ...prev, [slot]: '' }));
  };

  const handleResetAll = () => {
    const reset = {};
    Object.keys(MODEL_SLOT_LABELS).forEach((slot) => { reset[slot] = ''; });
    setModelOverrides(reset);
  };

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    if (!config) return Object.values(modelOverrides).some((v) => v !== '');
    const saved = config.models || {};
    return Object.keys(MODEL_SLOT_LABELS).some((slot) => {
      const savedVal = saved[slot] || '';
      const currentVal = modelOverrides[slot] || '';
      return savedVal !== currentVal;
    });
  }, [config, modelOverrides]);

  const handleSaveModels = async () => {
    setSaving(true);
    try {
      // Build the models payload — only send non-empty values, send null for cleared slots
      const models = {};
      Object.keys(MODEL_SLOT_LABELS).forEach((slot) => {
        models[slot] = modelOverrides[slot] || null;
      });
      await axios.put(`/api/internal/org-config/${orgId}`, { models });
      toast.success('AI model assignments saved');
      // Reload both effective and raw configs so overrides are correct
      await loadAll();
    } catch (err) {
      console.error('Failed to save models:', err);
      toast.error(err?.response?.data?.message || 'Failed to save model assignments');
    } finally {
      setSaving(false);
    }
  };

  // Count how many slots have custom overrides
  const overrideCount = Object.values(modelOverrides).filter((v) => v && v !== '').length;

  // ── Feature toggle handler ────────────────────────────────────
  const handleFeatureToggle = async (key, newValue) => {
    // Optimistic UI
    setFeatureOverrides((prev) => {
      const next = { ...prev, [key]: newValue };
      if (key === 'multi_module_assessment' && !newValue) {
        next.multi_module_table_scores = false;
      }
      return next;
    });
    setSavingFeatures(true);
    try {
      const features = { [key]: newValue };
      if (key === 'multi_module_assessment' && !newValue) {
        features.multi_module_table_scores = false;
      }
      await axios.put(`/api/internal/org-config/${orgId}`, { features });
      toast.success(`${CUSTOM_FEATURES.find((f) => f.key === key)?.label || key} ${newValue ? 'enabled' : 'disabled'}`);
      // Reload config to stay in sync
      const { data } = await axios.get(`/api/internal/org-config/${orgId}`);
      setConfig(data?.config || null);
    } catch (err) {
      // Revert on failure
      setFeatureOverrides((prev) => ({ ...prev, [key]: !newValue }));
      console.error('Failed to toggle feature:', err);
      toast.error(err?.response?.data?.message || 'Failed to save feature toggle');
    } finally {
      setSavingFeatures(false);
    }
  };
  // ── Feature threshold save handler ────────────────────────
  const handleSaveThreshold = async (threshold, value, featureLabel) => {
    const { limitKey } = threshold;
    const parsed = parseInt(value, 10);
    if (threshold.options) {
      if (!threshold.options.some((o) => o.value === parsed)) {
        toast.error('Please choose one of the listed options');
        return;
      }
    } else {
      const min = threshold.min ?? 1;
      const max = threshold.max ?? 100;
      if (isNaN(parsed) || parsed < min || parsed > max) {
        toast.error(`Threshold must be a number between ${min} and ${max}`);
        return;
      }
    }
    setSavingThreshold(true);
    try {
      await axios.put(`/api/internal/org-config/${orgId}`, { limits: { [limitKey]: parsed } });
      toast.success(`${featureLabel} setting saved`);
      await loadAll();
    } catch (err) {
      console.error('Failed to save threshold:', err);
      toast.error(err?.response?.data?.message || 'Failed to save threshold');
    } finally {
      setSavingThreshold(false);
    }
  };

  // ── Limits save handler ────────────────────────────────────
  const handleSaveLimits = async () => {
    setSavingLimits(true);
    try {
      // Convert empty strings to null to use platform defaults
      const limits = {
        max_questions_per_interview: limitOverrides.max_questions_per_interview === '' ? null : Number(limitOverrides.max_questions_per_interview),
        max_questions_per_assessment: limitOverrides.max_questions_per_assessment === '' ? null : Number(limitOverrides.max_questions_per_assessment),
        proctoring_strictness: Number(limitOverrides.proctoring_strictness) || 3,
      };
      await axios.put(`/api/internal/org-config/${orgId}`, { limits });
      toast.success('Limits saved successfully');
      await loadAll();
    } catch (err) {
      console.error('Failed to save limits:', err);
      toast.error(err?.response?.data?.message || 'Failed to save limits');
    } finally {
      setSavingLimits(false);
    }
  };

  const handleSaveWeights = async () => {
    const combos = [
      { key: 'all_three', keys: ['resume', 'assessment', 'interview'], label: 'All 3 modules' },
      { key: 'res_assess', keys: ['resume', 'assessment'], label: 'Resume + Assessment' },
      { key: 'res_interview', keys: ['resume', 'interview'], label: 'Resume + Interview' },
      { key: 'assess_interview', keys: ['assessment', 'interview'], label: 'Assessment + Interview' }
    ];
    
    for (const combo of combos) {
      const data = weightsConfigData[combo.key];
      const hasValue = combo.keys.some(k => data[k] !== '' && data[k] != null);
      if (hasValue) {
        let sum = 0;
        for (const k of combo.keys) {
          const val = parseFloat(data[k]);
          if (isNaN(val) || val < 0) {
            toast.error(`${combo.label}: Please enter valid positive numbers or leave all blank`);
            return;
          }
          sum += val;
        }
        if (sum !== 100) {
          toast.error(`${combo.label}: Weights must sum to 100%`);
          return;
        }
      }
    }

    setSavingWeights(true);
    try {
      await axios.put(`/api/internal/org-config/${orgId}`, { scoreWeights: weightsConfigData });
      toast.success('Score weights saved successfully');
      setShowWeightsModal(false);
      await loadAll();
    } catch (err) {
      console.error('Failed to save weights:', err);
      toast.error(err?.response?.data?.message || 'Failed to save weights');
    } finally {
      setSavingWeights(false);
    }
  };


  if (loading) {
    return <div className="flex items-center justify-center py-12"><Spinner className="h-5 w-5" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-800">Client Configurations</h4>
            <p className="text-xs text-gray-500 mt-1">Manage features, AI models, and limits for {orgName}</p>
          </div>
        </div>

        {/* Section tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { key: 'features', label: 'Feature Toggles', icon: Layers },
            { key: 'models', label: 'AI Model Assignment', icon: Sparkles },
            { key: 'limits', label: 'Limits', icon: AlertTriangle },
            { key: 'applyFields', label: 'Apply Link Fields', icon: FileText },
            { key: 'screeningRules', label: 'Screening Rules', icon: FileCheck },
            { key: 'tableLayout', label: 'Table Layout', icon: Columns },
            { key: 'qualifyingAgent', label: 'Qualifying Agent', icon: PhoneCallIcon },
          ].map((s) => (
            <button
              key={s.key}
              onClick={() => setActiveSection(s.key)}
              className={cn(
                'inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                activeSection === s.key
                  ? 'bg-[#007BFF] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              )}
            >
              <s.icon className="h-4 w-4" />
              {s.label}
            </button>
          ))}
        </div>

        {/* Feature Toggles Section */}
        {activeSection === 'features' && (
          <div className="space-y-4">
            <div className="rounded-lg border border-gray-200 bg-gray-50/50 overflow-hidden divide-y divide-gray-200">
              {CUSTOM_FEATURES.map(({ key, label, desc, defaultEnabled, threshold, requiresFeature }) => {
                const enabled = featureOverrides[key] ?? defaultEnabled;
                const parentEnabled = requiresFeature
                  ? (featureOverrides[requiresFeature] ??
                    CUSTOM_FEATURES.find((f) => f.key === requiresFeature)?.defaultEnabled ??
                    false)
                  : true;
                const toggleDisabled = savingFeatures || (requiresFeature && !parentEnabled);
                const currentThresholdVal = threshold ? (thresholdOverrides[threshold.limitKey] ?? String(threshold.platformDefault)) : null;
                return (
                  <div key={key} className="px-4 py-4">
                    <div className="flex items-center gap-4">
                      {/* Label & description */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-800">{label}</span>
                          <span className={cn(
                            'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
                            enabled
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-red-100 text-red-600'
                          )}>
                            <span className={cn('h-1.5 w-1.5 rounded-full', enabled ? 'bg-emerald-500' : 'bg-red-500')} />
                            {enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">{desc}</p>
                        {requiresFeature && !parentEnabled && (
                          <p className="text-xs text-amber-600 mt-1">
                            Enable {CUSTOM_FEATURES.find((f) => f.key === requiresFeature)?.label || requiresFeature} first.
                          </p>
                        )}
                      </div>

                      {/* Toggle switch */}
                      <button
                        type="button"
                        role="switch"
                        aria-checked={enabled}
                        disabled={toggleDisabled}
                        onClick={() => {
                          if (toggleDisabled) return;
                          handleFeatureToggle(key, !enabled);
                        }}
                        className={cn(
                          'relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#007BFF]/30 shrink-0',
                          enabled ? 'bg-[#007BFF]' : 'bg-gray-300',
                          toggleDisabled && 'opacity-50 cursor-not-allowed'
                        )}
                      >
                        <span
                          className={cn(
                            'inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200',
                            enabled ? 'translate-x-6' : 'translate-x-1'
                          )}
                        />
                      </button>
                    </div>

                    {/* Threshold input — shown only when feature is enabled */}
                    {threshold && enabled && (
                      <div className="mt-3 flex items-center gap-2 pl-0">
                        <label className="text-xs text-gray-500 shrink-0">{threshold.label}:</label>
                        {threshold.options ? (
                          <select
                            // A saved value the dropdown no longer offers shows as the default,
                            // which is also what the server resolves it to.
                            value={
                              threshold.options.some((o) => String(o.value) === currentThresholdVal)
                                ? currentThresholdVal
                                : String(threshold.platformDefault)
                            }
                            onChange={(e) => setThresholdOverrides((prev) => ({ ...prev, [threshold.limitKey]: e.target.value }))}
                            className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-800 focus:border-[#007BFF] focus:outline-none focus:ring-1 focus:ring-[#007BFF]/30"
                          >
                            {threshold.options.map((o) => (
                              <option key={o.value} value={String(o.value)}>{o.label}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="number"
                            min={threshold.min}
                            max={threshold.max}
                            value={currentThresholdVal}
                            onChange={(e) => setThresholdOverrides((prev) => ({ ...prev, [threshold.limitKey]: e.target.value }))}
                            className="w-20 rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-800 focus:border-[#007BFF] focus:outline-none focus:ring-1 focus:ring-[#007BFF]/30"
                          />
                        )}
                        <button
                          type="button"
                          disabled={savingThreshold}
                          onClick={() =>
                            handleSaveThreshold(
                              threshold,
                              threshold.options &&
                                !threshold.options.some((o) => String(o.value) === currentThresholdVal)
                                ? String(threshold.platformDefault)
                                : currentThresholdVal,
                              label
                            )
                          }
                          className="inline-flex items-center gap-1 rounded-md bg-[#007BFF] px-2.5 py-1 text-xs font-medium text-white hover:bg-[#0069d9] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {savingThreshold ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              <div className="px-4 py-4">
                <div className="flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-800">Custom Score Weights</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">Configure how overall scores are weighted when combining different modules.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowWeightsModal(true)}
                    className="inline-flex items-center gap-1 rounded-md bg-[#007BFF] px-2.5 py-1.5 text-xs font-medium text-white hover:bg-[#0069d9] transition-colors shrink-0"
                  >
                    <Edit3 className="h-3.5 w-3.5" /> Configure Weights
                  </button>
                </div>
              </div>
            </div>


          </div>
        )}

        {/* AI Model Assignment Section */}
        {activeSection === 'models' && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-400 mt-0.5">{overrideCount} of {Object.keys(MODEL_SLOT_LABELS).length} slots customised</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleResetAll}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  <RotateCcw className="h-3 w-3" /> Reset All
                </button>
                <button
                  type="button"
                  disabled={!hasChanges || saving}
                  onClick={() => setShowSaveConfirm(true)}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
                    hasChanges && !saving
                      ? 'bg-[#007BFF] text-white hover:bg-[#0069d9]'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  )}
                >
                  {saving ? <Spinner className="h-3 w-3" /> : <Check className="h-3 w-3" />}
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </div>

            {/* Cost tier legend */}
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <span className="text-gray-500 font-medium">Cost tiers:</span>
              {Object.entries(TIER_BADGE).map(([key, badge]) => (
                <span key={key} className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium', badge.className)}>
                  {badge.label}
                </span>
              ))}
            </div>

            {/* Model slot groups */}
            {MODEL_SLOT_GROUPS.map((group) => (
              <div key={group.title} className="rounded-lg border border-gray-200 bg-gray-50/50 overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-100/80 border-b border-gray-200">
                  <group.icon className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-semibold text-gray-700">{group.title}</span>
                </div>
                <div className="divide-y divide-gray-200">
                  {group.slots.map((slot) => {
                    const meta = MODEL_SLOT_LABELS[slot];
                    const allowedModels = modelAllowlist[slot] || [];
                    const currentValue = modelOverrides[slot] || '';
                    const platformDefault = PLATFORM_DEFAULTS[slot];
                    const effectiveModel = currentValue || platformDefault;
                    const effectiveMeta = modelLookup[effectiveModel];
                    const isCustom = currentValue !== '';
                    const tierInfo = effectiveMeta ? TIER_BADGE[effectiveMeta.tier] : null;

                    return (
                      <div key={slot} className="flex items-center gap-4 px-4 py-3">
                        {/* Label & description */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-800">{meta.label}</span>
                            {isCustom && (
                              <span className="inline-flex items-center rounded-full bg-blue-100 text-blue-700 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                                Custom
                              </span>
                            )}
                            {tierInfo && (
                              <span className={cn('inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold', tierInfo.className)}>
                                {tierInfo.label}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-400 mt-0.5">{meta.desc}</p>
                        </div>

                        {/* Model selector */}
                        <div className="flex items-center gap-2 shrink-0">
                          <select
                            value={currentValue}
                            onChange={(e) => handleModelChange(slot, e.target.value)}
                            className={cn(
                              'rounded-lg border px-3 py-1.5 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#007BFF]/30 focus:border-[#007BFF] min-w-[260px]',
                              isCustom
                                ? 'border-blue-300 bg-blue-50/50 text-gray-900'
                                : 'border-gray-200 bg-white text-gray-500'
                            )}
                          >
                            <option value="">Platform Default ({platformDefault})</option>
                            {allowedModels.map((modelId) => {
                              const m = modelLookup[modelId];
                              const costLabel = m ? ` — ₹${m.costPer1M?.inputInr ?? 0}/₹${m.costPer1M?.outputInr ?? 0} per 1M` : '';
                              return (
                                <option key={modelId} value={modelId}>
                                  {modelId}{costLabel}
                                </option>
                              );
                            })}
                          </select>
                          {isCustom && (
                            <button
                              type="button"
                              onClick={() => handleResetSlot(slot)}
                              className="rounded-lg p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                              title="Reset to platform default"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Info box */}

          </div>
        )}


        {/* Limits Section */}
        {activeSection === 'limits' && (() => {
          const STRICTNESS_OPTIONS = [
            { level: 1, label: 'Lenient', color: 'bg-emerald-500', text: 'text-emerald-700', bgLight: 'bg-emerald-50 border-emerald-200', desc: '0.6x Risk Multiplier. Tolerant of minor movements/tab switches. Flagged: ≥70 pts, High Risk: ≥120 pts.' },
            { level: 2, label: 'Moderate', color: 'bg-lime-500', text: 'text-lime-700', bgLight: 'bg-lime-50 border-lime-200', desc: '0.8x Risk Multiplier. Slightly relaxed monitoring with moderate thresholds. Flagged: ≥60 pts, High Risk: ≥110 pts.' },
            { level: 3, label: 'Standard', color: 'bg-blue-500', text: 'text-blue-700', bgLight: 'bg-blue-50 border-blue-200', desc: '1.0x Risk Multiplier. Default platform baseline monitoring. Flagged: ≥50 pts, High Risk: ≥100 pts.' },
            { level: 4, label: 'Strict', color: 'bg-amber-500', text: 'text-amber-700', bgLight: 'bg-amber-50 border-amber-200', desc: '1.3x Risk Multiplier. High sensitivity for Tab Switch / Face / Speaker detection. Flagged: ≥35 pts, High Risk: ≥80 pts.' },
            { level: 5, label: 'Extreme', color: 'bg-rose-500', text: 'text-rose-700', bgLight: 'bg-rose-50 border-rose-200', desc: '1.6x Risk Multiplier. Maximum security enforcement. Flagged: ≥25 pts, High Risk: ≥60 pts.' },
          ];

          const currentLevel = Math.max(1, Math.min(5, Number(limitOverrides.proctoring_strictness) || 3));
          const currentStrictness = STRICTNESS_OPTIONS.find((s) => s.level === currentLevel) || STRICTNESS_OPTIONS[2];

          return (
            <div className="space-y-6">
              <p className="text-xs text-gray-500">Set per-organisation limits for proctoring strictness, interview question caps, and assessment limits.</p>

              {/* Proctoring Strictness Meter Card */}
              <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-4 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-100 pb-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <Shield className="h-5 w-5 text-[#007BFF]" />
                      <h4 className="text-base font-semibold text-gray-900">Proctoring Strictness Meter</h4>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Configure how strictly proctoring events (AI Video frame analysis, phone detection, face absence, multi-speaker, tab switches) flag candidates and deduct points for AI Video/Voice Interviews & Assessments.
                    </p>
                  </div>
                  <div className={cn('inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border self-start sm:self-auto', currentStrictness.bgLight, currentStrictness.text)}>
                    <span className={cn('h-2 w-2 rounded-full', currentStrictness.color)} />
                    Level {currentStrictness.level} • {currentStrictness.label}
                  </div>
                </div>

                {/* Meter Bar */}
                <div className="space-y-1.5 pt-1">
                  <div className="flex items-center justify-between text-xs font-medium text-gray-600">
                    <span>Proctoring Sensitivity Meter</span>
                    <span className={cn('font-bold', currentStrictness.text)}>
                      Risk Multiplier: {currentStrictness.desc.split('.')[0]}
                    </span>
                  </div>

                  <div className="grid grid-cols-5 gap-2 h-3.5 rounded-full bg-gray-100 p-1">
                    {[1, 2, 3, 4, 5].map((lvl) => {
                      const isActive = currentLevel >= lvl;
                      const isCurrent = currentLevel === lvl;
                      const colorMap = {
                        1: 'bg-emerald-500',
                        2: 'bg-lime-500',
                        3: 'bg-blue-500',
                        4: 'bg-amber-500',
                        5: 'bg-rose-500',
                      };
                      return (
                        <div
                          key={lvl}
                          className={cn(
                            'h-full rounded-sm transition-all duration-200',
                            isActive ? colorMap[lvl] : 'bg-gray-200',
                            isCurrent ? 'ring-2 ring-offset-1 ring-[#007BFF] shadow-sm' : 'opacity-60'
                          )}
                        />
                      );
                    })}
                  </div>
                </div>

                {/* Slider Input */}
                <div className="space-y-2 pt-2">
                  <input
                    type="range"
                    min="1"
                    max="5"
                    step="1"
                    value={currentLevel}
                    onChange={(e) => setLimitOverrides((prev) => ({ ...prev, proctoring_strictness: Number(e.target.value) }))}
                    className="w-full h-2.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#007BFF]"
                  />
                  <div className="flex justify-between text-[11px] font-medium text-gray-500 px-0.5">
                    <button type="button" onClick={() => setLimitOverrides((prev) => ({ ...prev, proctoring_strictness: 1 }))} className={cn('hover:text-emerald-600 transition-colors', currentLevel === 1 && 'font-bold text-emerald-600')}>1 • Lenient</button>
                    <button type="button" onClick={() => setLimitOverrides((prev) => ({ ...prev, proctoring_strictness: 2 }))} className={cn('hover:text-lime-600 transition-colors', currentLevel === 2 && 'font-bold text-lime-600')}>2 • Moderate</button>
                    <button type="button" onClick={() => setLimitOverrides((prev) => ({ ...prev, proctoring_strictness: 3 }))} className={cn('hover:text-blue-600 transition-colors', currentLevel === 3 && 'font-bold text-blue-600')}>3 • Standard (Default)</button>
                    <button type="button" onClick={() => setLimitOverrides((prev) => ({ ...prev, proctoring_strictness: 4 }))} className={cn('hover:text-amber-600 transition-colors', currentLevel === 4 && 'font-bold text-amber-600')}>4 • Strict</button>
                    <button type="button" onClick={() => setLimitOverrides((prev) => ({ ...prev, proctoring_strictness: 5 }))} className={cn('hover:text-rose-600 transition-colors', currentLevel === 5 && 'font-bold text-rose-600')}>5 • Extreme</button>
                  </div>
                </div>

                {/* Description Details Box */}
                <div className={cn('rounded-lg p-3 border text-xs leading-relaxed space-y-1 mt-2', currentStrictness.bgLight)}>
                  <div className="font-semibold text-gray-800">
                    Active Policy: {currentStrictness.label} (Level {currentStrictness.level})
                  </div>
                  <p className="text-gray-600">{currentStrictness.desc}</p>
                </div>
              </div>

              {/* Limits Inputs */}
              <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-5 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Interview Questions</label>
                    <p className="text-xs text-gray-500 mb-2">Maximum number of AI questions generated per interview. (Default: {PLATFORM_DEFAULTS?.limits?.max_questions_per_interview || 30})</p>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        max="100"
                        className="w-full max-w-[200px] rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#007BFF] focus:outline-none focus:ring-1 focus:ring-[#007BFF]"
                        placeholder="Use default"
                        value={limitOverrides.max_questions_per_interview || ''}
                        onChange={(e) => setLimitOverrides((prev) => ({ ...prev, max_questions_per_interview: e.target.value }))}
                      />
                      {limitOverrides.max_questions_per_interview !== '' && (
                        <button
                          type="button"
                          onClick={() => setLimitOverrides((prev) => ({ ...prev, max_questions_per_interview: '' }))}
                          className="text-xs text-red-500 hover:text-red-700"
                        >
                          Reset
                        </button>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Assessment Questions</label>
                    <p className="text-xs text-gray-500 mb-2">Maximum number of questions generated per assessment. (Default: {PLATFORM_DEFAULTS?.limits?.max_questions_per_assessment || 40})</p>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        max="100"
                        className="w-full max-w-[200px] rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#007BFF] focus:outline-none focus:ring-1 focus:ring-[#007BFF]"
                        placeholder="Use default"
                        value={limitOverrides.max_questions_per_assessment || ''}
                        onChange={(e) => setLimitOverrides((prev) => ({ ...prev, max_questions_per_assessment: e.target.value }))}
                      />
                      {limitOverrides.max_questions_per_assessment !== '' && (
                        <button
                          type="button"
                          onClick={() => setLimitOverrides((prev) => ({ ...prev, max_questions_per_assessment: '' }))}
                          className="text-xs text-red-500 hover:text-red-700"
                        >
                          Reset
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200 flex justify-end">
                  <button
                    onClick={handleSaveLimits}
                    disabled={savingLimits}
                    className="inline-flex items-center justify-center rounded-lg bg-[#007BFF] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-600 disabled:opacity-50"
                  >
                    {savingLimits ? (
                      <>
                        <Spinner className="mr-2 h-4 w-4" /> Saving...
                      </>
                    ) : (
                      'Save Limits'
                    )}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Save Confirmation Modal */}
        {showSaveConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4 backdrop-blur-sm">
            <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
              <div className="p-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                    <AlertTriangle className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">Confirm Model Changes</h3>
                    <p className="mt-2 text-sm text-gray-600">
                      Changing the assigned AI models may enhance or deteriorate the functionality bound to them. Costs per execution may also change depending on the new models selected.
                    </p>
                    <p className="mt-2 text-sm text-gray-600">
                      Are you sure you want to proceed with these changes?
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 rounded-b-xl border-t border-gray-100 bg-gray-50 px-6 py-4">
                <button
                  type="button"
                  onClick={() => setShowSaveConfirm(false)}
                  className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowSaveConfirm(false);
                    handleSaveModels();
                  }}
                  className="inline-flex items-center gap-2 rounded-lg bg-[#007BFF] px-4 py-2 text-sm font-medium text-white hover:bg-[#0069d9] transition-colors disabled:opacity-50"
                  disabled={saving}
                >
                  Confirm Changes
                </button>
              </div>
            </div>
          </div>
        )}


      {/* Score Weights Modal */}

      {showWeightsModal && (

        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm">

          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col border border-gray-100">

            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/60">

              <div>

                <h3 className="text-base font-semibold text-gray-900">Configure Score Weights</h3>

                <p className="text-xs text-gray-500 mt-0.5">Each filled row must add up to exactly 100%. Leave blank to use equal averages for that combination.</p>

              </div>

              <button type="button" onClick={() => setShowWeightsModal(false)} className="rounded-lg p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none transition-colors">

                <X className="h-5 w-5" />

              </button>

            </div>

            <div className="p-6 overflow-y-auto max-h-[60vh] space-y-5">

              {[

                { key: 'all_three', label: 'All 3', desc: 'Resume + Assessment + Interview', fields: [{ id: 'resume', label: 'Resume' }, { id: 'assessment', label: 'Assess' }, { id: 'interview', label: 'Interview' }] },

                { key: 'res_assess', label: 'Resume scoring + Online Assessment', desc: '', fields: [{ id: 'resume', label: 'Resume' }, { id: 'assessment', label: 'Assess' }] },

                { key: 'res_interview', label: 'Resume scoring + AI Interview', desc: '', fields: [{ id: 'resume', label: 'Resume' }, { id: 'interview', label: 'Interview' }] },

                { key: 'assess_interview', label: 'Online Assessment + AI Interview', desc: '', fields: [{ id: 'assessment', label: 'Assess' }, { id: 'interview', label: 'Interview' }] },

              ].map((row) => {

                const rowData = weightsConfigData[row.key] || {};

                const sum = row.fields.reduce((acc, f) => { const v = parseFloat(rowData[f.id]); return acc + (isNaN(v) ? 0 : v); }, 0);

                const hasAnyValue = row.fields.some((f) => rowData[f.id] !== '' && rowData[f.id] != null);

                const isValid = !hasAnyValue || Math.abs(sum - 100) < 0.01;

                return (

                  <div key={row.key} className="rounded-xl border border-gray-200 bg-gray-50/50 p-4 space-y-3">

                    <div className="flex items-center justify-between">

                      <div>

                        <p className="text-sm font-medium text-gray-800">{row.label}</p>

                        <p className="text-xs text-gray-400">{row.desc}</p>

                      </div>

                      {hasAnyValue && (

                        <span className={cn('text-xs font-semibold px-2 py-0.5 rounded-full', isValid ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600')}>

                          {sum}% {isValid ? '\u2713' : '\u2260 100'}

                        </span>

                      )}

                    </div>

                    <div className="flex flex-wrap gap-3">

                      {row.fields.map((f) => (

                        <div key={f.id} className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-3 py-2">

                          <span className="text-xs text-gray-500 font-medium shrink-0">{f.label}:</span>

                          <input

                            type="number"

                            min="0"

                            max="100"

                            step="any"

                            placeholder="—"

                            className="w-14 bg-transparent border-0 focus:outline-none text-sm font-semibold text-gray-900 text-center placeholder:text-gray-300"

                            value={rowData[f.id] ?? ''}

                            onChange={(e) => setWeightsConfigData((prev) => ({ ...prev, [row.key]: { ...prev[row.key], [f.id]: e.target.value } }))}

                          />

                          <span className="text-xs text-gray-400">%</span>

                        </div>

                      ))}

                    </div>

                  </div>

                );

              })}

            </div>

            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-3 shrink-0">

              <button type="button" onClick={() => setShowWeightsModal(false)} className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">Cancel</button>

              <button type="button" disabled={savingWeights} onClick={handleSaveWeights} className="inline-flex items-center gap-2 rounded-lg bg-[#007BFF] px-4 py-2 text-sm font-medium text-white hover:bg-[#0069d9] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm">

                {savingWeights && <span className="w-4 h-4 border-2 border-white/80 border-t-transparent rounded-full animate-spin" />}

                {savingWeights ? 'Saving...' : 'Save Weights'}

              </button>

            </div>

          </div>

        </div>
      )}

      {/* Apply Link Fields Section */}
      {activeSection === 'applyFields' && (
        <ApplyLinkFieldsAdmin orgId={orgId} />
      )}

      {/* Screening Rules Section */}
      {activeSection === 'screeningRules' && (
        <ScreeningRulesAdmin orgId={orgId} />
      )}

      {/* Table Layout Section */}
      {activeSection === 'tableLayout' && (
        <TableLayoutAdmin orgId={orgId} />
      )}

      {/* Qualifying Agent: call retries */}
      {activeSection === 'qualifyingAgent' && (
        <QualifyingAgentAdmin orgId={orgId} />
      )}

      </div>
    </div>
  );
}

const OrgLibraryTab = ({ orgId, orgName }) => {
  const [activeSection, setActiveSection] = useState('assessments');
  const [assessments, setAssessments] = useState([]);
  const [assessmentsLoading, setAssessmentsLoading] = useState(true);
  const [assessmentPagination, setAssessmentPagination] = useState({ current: 1, total: 1, totalCount: 0 });
  const [assessmentSearch, setAssessmentSearch] = useState('');
  const [assessmentCategory, setAssessmentCategory] = useState('');
  const [assessmentDifficulty, setAssessmentDifficulty] = useState('');
  const [assessmentFilters, setAssessmentFilters] = useState({ categories: [], difficulties: [] });
  const [assessmentPage, setAssessmentPage] = useState(1);
  const [viewAssessmentId, setViewAssessmentId] = useState(null);

  const [interviews, setInterviews] = useState([]);
  const [interviewsLoading, setInterviewsLoading] = useState(true);
  const [interviewPagination, setInterviewPagination] = useState({ current: 1, total: 1, totalCount: 0 });
  const [interviewSearch, setInterviewSearch] = useState('');
  const [interviewCategory, setInterviewCategory] = useState('');
  const [interviewDifficulty, setInterviewDifficulty] = useState('');
  const [interviewPage, setInterviewPage] = useState(1);
  const [viewInterviewId, setViewInterviewId] = useState(null);

  const PAGE_LIMIT = 8;

  const loadAssessmentFilters = useCallback(async () => {
    try {
      const data = await libraryApi.getAssessmentsFilters({ orgId });
      setAssessmentFilters({
        categories: data?.categories || [],
        difficulties: data?.difficulties || [],
      });
    } catch (error) {
      console.error('Failed to load library filters', error);
    }
  }, [orgId]);

  useEffect(() => {
    loadAssessmentFilters();
  }, [loadAssessmentFilters]);

  const loadAssessments = useCallback(async () => {
    setAssessmentsLoading(true);
    try {
      const response = await libraryApi.getAssessmentsStats({
        orgId,
        page: assessmentPage,
        limit: PAGE_LIMIT,
        search: assessmentSearch || undefined,
        category: assessmentCategory || undefined,
        difficulty: assessmentDifficulty || undefined,
      });
      setAssessments(response?.assessments || []);
      setAssessmentPagination(response?.pagination || { current: 1, total: 1, totalCount: 0 });
    } catch (error) {
      console.error('Failed to load organisation assessments library', error);
      toast.error('Failed to load assessments library');
    } finally {
      setAssessmentsLoading(false);
    }
  }, [orgId, assessmentPage, assessmentSearch, assessmentCategory, assessmentDifficulty]);

  useEffect(() => {
    loadAssessments();
  }, [loadAssessments]);

  const loadInterviews = useCallback(async () => {
    setInterviewsLoading(true);
    try {
      const response = await libraryApi.getAIInterviewsStats({
        orgId,
        page: interviewPage,
        limit: PAGE_LIMIT,
        search: interviewSearch || undefined,
        category: interviewCategory || undefined,
        difficulty: interviewDifficulty || undefined,
      });
      setInterviews(response?.interviews || []);
      setInterviewPagination(response?.pagination || { current: 1, total: 1, totalCount: 0 });
    } catch (error) {
      console.error('Failed to load organisation interview library', error);
      toast.error('Failed to load AI interviews library');
    } finally {
      setInterviewsLoading(false);
    }
  }, [orgId, interviewPage, interviewSearch, interviewCategory, interviewDifficulty]);

  useEffect(() => {
    loadInterviews();
  }, [loadInterviews]);

  const assessmentCategories = assessmentFilters.categories?.length
    ? assessmentFilters.categories
    : ['General', 'Technical', 'Behavioral'];
  const assessmentDifficulties = assessmentFilters.difficulties?.length
    ? assessmentFilters.difficulties
    : ['Easy', 'Medium', 'Hard'];

  const tabs = [
    { key: 'assessments', label: 'Assessments' },
    { key: 'interviews', label: 'AI Interviews' },
  ];

  const renderPagination = (pagination, onPrev, onNext) => {
    if (!pagination || pagination.total <= 1) return null;
    return (
      <div className="flex items-center justify-between text-xs text-gray-500 mt-4">
        <span>
          Page {pagination.current} of {pagination.total}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onPrev}
            disabled={pagination.current <= 1}
            className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={pagination.current >= pagination.total}
            className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  const renderAssessments = () => (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={assessmentSearch}
            onChange={(e) => {
              setAssessmentSearch(e.target.value);
              setAssessmentPage(1);
            }}
            placeholder="Search assessments..."
            className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-[#007BFF]/30 focus:border-[#007BFF]"
          />
        </div>
        <select
          value={assessmentCategory}
          onChange={(e) => {
            setAssessmentCategory(e.target.value);
            setAssessmentPage(1);
          }}
          className="px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-700"
        >
          <option value="">All categories</option>
          {assessmentCategories.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
        <select
          value={assessmentDifficulty}
          onChange={(e) => {
            setAssessmentDifficulty(e.target.value);
            setAssessmentPage(1);
          }}
          className="px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-700"
        >
          <option value="">All difficulty</option>
          {assessmentDifficulties.map((level) => (
            <option key={level} value={level}>
              {level}
            </option>
          ))}
        </select>
      </div>

      {assessmentsLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner className="h-5 w-5" />
        </div>
      ) : assessments.length === 0 ? (
        <EmptyState text="No assessments available for this organisation." />
      ) : (
        <div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-xs uppercase text-gray-500">
                  <th className="text-left px-3 py-2">Assessment</th>
                  <th className="text-center px-3 py-2">Screenings</th>
                  <th className="text-center px-3 py-2">Answered</th>
                  <th className="text-center px-3 py-2">Avg</th>
                  <th className="text-center px-3 py-2">Median</th>
                  <th className="text-left px-3 py-2">Category</th>
                  <th className="text-center px-3 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {assessments.map((row) => (
                  <tr key={row._id} className="border-b border-gray-100 last:border-0">
                    <td className="px-3 py-3">
                      <div>
                        <p className="font-semibold text-gray-900">{row.title}</p>
                        {row.description && (
                          <p className="text-xs text-gray-500 line-clamp-1">{row.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-center text-gray-700">{row.stats?.screeningsCount ?? 0}</td>
                    <td className="px-3 py-3 text-center text-gray-700">{row.stats?.totalAnswered ?? 0}</td>
                    <td className="px-3 py-3 text-center text-gray-700">
                      {row.stats?.avgScorePercent != null ? `${row.stats.avgScorePercent}%` : '—'}
                    </td>
                    <td className="px-3 py-3 text-center text-gray-700">
                      {row.stats?.medianScorePercent != null ? `${row.stats.medianScorePercent}%` : '—'}
                    </td>
                    <td className="px-3 py-3 text-gray-700">{row.category || '—'}</td>
                    <td className="px-3 py-3 text-center">
                      <button
                        type="button"
                        onClick={() => setViewAssessmentId(row._id)}
                        className="text-[#007BFF] hover:underline font-medium"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {renderPagination(
            assessmentPagination,
            () => setAssessmentPage((prev) => Math.max(1, prev - 1)),
            () => setAssessmentPage((prev) => prev + 1)
          )}
        </div>
      )}
    </div>
  );

  const renderInterviews = () => (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={interviewSearch}
            onChange={(e) => {
              setInterviewSearch(e.target.value);
              setInterviewPage(1);
            }}
            placeholder="Search AI interviews..."
            className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-[#007BFF]/30 focus:border-[#007BFF]"
          />
        </div>
        <select
          value={interviewCategory}
          onChange={(e) => {
            setInterviewCategory(e.target.value);
            setInterviewPage(1);
          }}
          className="px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-700"
        >
          <option value="">All categories</option>
          {['Sales', 'Software Development', 'Marketing', 'General', 'IT', 'Engineering'].map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
        <select
          value={interviewDifficulty}
          onChange={(e) => {
            setInterviewDifficulty(e.target.value);
            setInterviewPage(1);
          }}
          className="px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-700"
        >
          <option value="">All difficulty</option>
          {['Easy', 'Medium', 'Hard'].map((diff) => (
            <option key={diff} value={diff}>
              {diff}
            </option>
          ))}
        </select>
      </div>

      {interviewsLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner className="h-5 w-5" />
        </div>
      ) : interviews.length === 0 ? (
        <EmptyState text="No AI interviews available for this organisation." />
      ) : (
        <div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-xs uppercase text-gray-500">
                  <th className="text-left px-3 py-2">Interview</th>
                  <th className="text-center px-3 py-2">Screenings</th>
                  <th className="text-center px-3 py-2">Answered</th>
                  <th className="text-center px-3 py-2">Avg</th>
                  <th className="text-center px-3 py-2">Median</th>
                  <th className="text-left px-3 py-2">Category</th>
                  <th className="text-center px-3 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {interviews.map((row) => (
                  <tr key={row._id} className="border-b border-gray-100 last:border-0">
                    <td className="px-3 py-3">
                      <div>
                        <p className="font-semibold text-gray-900">{row.title}</p>
                        {row.description && (
                          <p className="text-xs text-gray-500 line-clamp-1">{row.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-center text-gray-700">{row.stats?.screeningsCount ?? 0}</td>
                    <td className="px-3 py-3 text-center text-gray-700">{row.stats?.totalAnswered ?? 0}</td>
                    <td className="px-3 py-3 text-center text-gray-700">
                      {row.stats?.avgScorePercent != null ? `${row.stats.avgScorePercent}%` : '—'}
                    </td>
                    <td className="px-3 py-3 text-center text-gray-700">
                      {row.stats?.medianScorePercent != null ? `${row.stats.medianScorePercent}%` : '—'}
                    </td>
                    <td className="px-3 py-3 text-gray-700">{row.category || '—'}</td>
                    <td className="px-3 py-3 text-center">
                      <button
                        type="button"
                        onClick={() => setViewInterviewId(row._id)}
                        className="text-[#007BFF] hover:underline font-medium"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {renderPagination(
            interviewPagination,
            () => setInterviewPage((prev) => Math.max(1, prev - 1)),
            () => setInterviewPage((prev) => prev + 1)
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Library overview</h3>
          <p className="text-sm text-gray-500">
            Assessments and AI interviews created by <span className="font-medium text-gray-700">{orgName}</span>.
          </p>
        </div>
        <TabPills tabs={tabs} active={activeSection} onChange={setActiveSection} />
      </div>

      {activeSection === 'assessments' ? renderAssessments() : renderInterviews()}

      {viewAssessmentId && (
        <div className="fixed inset-0 z-[210] flex items-center justify-center px-4 bg-black/40 backdrop-blur-sm">
          <div className="fixed inset-0" onClick={() => setViewAssessmentId(null)} aria-hidden />
          <div
            className="relative w-full max-w-4xl h-[90vh] bg-white rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <AssessmentCreateFlowModal
              onClose={() => setViewAssessmentId(null)}
              skipInviteStep
              hasNextStep={false}
              viewOnly
              viewTestId={viewAssessmentId}
              libraryOrgId={orgId}
            />
          </div>
        </div>
      )}

      <AIInterviewViewModal
        open={Boolean(viewInterviewId)}
        interviewId={viewInterviewId}
        orgId={orgId}
        onClose={() => setViewInterviewId(null)}
      />
    </div>
  );
};

const ORG_TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'teams', label: 'Teams & Members' },
  { key: 'jobs', label: 'Job Postings' },
  { key: 'support', label: 'Support' },
  { key: 'billing', label: 'Billing & Invoices' },
  { key: 'dpa', label: 'DPA Management' },
  { key: 'config', label: 'Client Configurations' },
];

function OrgAccordion({ org, isExpanded, onToggle, detailCache, setDetailCache, setActiveView, isPlatformSuperAdmin = false, handleResumeScoreClick = null }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);

  const detail = detailCache.get(org.id);

  const tabs = useMemo(() => (
    isPlatformSuperAdmin
      ? [...ORG_TABS, { key: 'library', label: 'Library' }]
      : ORG_TABS
  ), [isPlatformSuperAdmin]);

  const loadDetail = useCallback(async () => {
    if (detailCache.has(org.id)) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`/api/internal/org-detail/${org.id}`);
      setDetailCache((prev) => {
        const next = new Map(prev);
        next.set(org.id, data);
        return next;
      });
    } catch {
      toast.error('Failed to load organisation details');
    } finally {
      setLoading(false);
    }
  }, [org.id, detailCache, setDetailCache]);

  const handleToggle = () => {
    const opening = !isExpanded;
    onToggle();
    if (opening) {
      loadDetail();
      setActiveTab('overview');
    }
  };

  const usagePct = org.usagePct || 0;
  const expiringDays = org.package_expiry_date
    ? daysBetween(new Date(), org.package_expiry_date)
    : Infinity;
  const isWarning = usagePct >= 80 || expiringDays <= 30;

  return (
    <div
      className={cn(
        'rounded-2xl border transition-colors overflow-hidden',
        isWarning
          ? 'border-rose-300 bg-rose-50/40'
          : 'border-gray-200 bg-white',
      )}
    >
      {/* collapsed header */}
      <button
        onClick={handleToggle}
        className="flex w-full items-center gap-4 px-5 py-4 text-left hover:bg-white/60 transition-colors"
      >
        {/* icon */}
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-blue-500 shadow-sm">
          <Building2 className="h-5 w-5 text-white" />
        </div>

        {/* name + badges */}
        <div className="flex flex-1 flex-wrap items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-gray-900 truncate">{org.name}</span>
          <Badge className={planColor(org.plan)}>{capitalize(org.plan)}</Badge>
          <Badge className={statusColor(org.status)}>{capitalize(org.status)}</Badge>
        </div>

        {/* quick stats */}
        <span className="hidden md:inline text-xs text-gray-400 shrink-0 tabular-nums">
          {org.members ?? 0} members · {org.teams ?? 0} teams · {org.support?.open ?? 0} open support
        </span>

        {/* usage indicator */}
        <div className="hidden sm:flex items-center gap-2 shrink-0 w-40">
          {usagePct >= 80 ? (
            <>
              <span className="text-xs font-medium text-red-600 tabular-nums">{fmtPct3(usagePct)}% credits used</span>
              <div className="flex-1">
                <ProgressBar pct={usagePct} colorClass="bg-red-500" />
              </div>
            </>
          ) : expiringDays <= 30 ? (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600">
              <AlertTriangle className="h-3.5 w-3.5" />
              Expires in {expiringDays}d
            </span>
          ) : (
            <>
              <span className="text-xs text-gray-400 tabular-nums">{fmtPct3(usagePct)}%</span>
              <div className="flex-1">
                <ProgressBar pct={usagePct} colorClass="bg-indigo-500" />
              </div>
            </>
          )}
        </div>

        {/* chevron */}
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-gray-400 shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
        )}
      </button>

      {/* expanded body */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="border-t border-gray-100 px-5 py-5 space-y-5">
              {loading && !detail ? (
                <div className="flex items-center justify-center py-10">
                  <Spinner className="h-6 w-6" />
                </div>
              ) : detail ? (
                <>
                  <TabPills tabs={tabs} active={activeTab} onChange={setActiveTab} />
                  <div className="mt-4">
                    {activeTab === 'overview' && (
                      <OverviewTab
                        detail={{ ...org, ...(detail.subscription || {}), llm: org.llm, ai: org.ai, otherUsage: detail.otherUsage, quotaUsage: detail.quotaUsage, quotas: detail.quotaUsage || org.quotas }}
                      />
                    )}
                    {activeTab === 'teams' && <TeamsTab detail={detail} />}
                    {activeTab === 'jobs' && <JobPostingsTab detail={detail} isPlatformSuperAdmin={isPlatformSuperAdmin} handleResumeScoreClick={handleResumeScoreClick} />}
                    {activeTab === 'support' && <SupportTab detail={detail} setActiveView={setActiveView} />}
                    {activeTab === 'billing' && <BillingTab detail={{ ...org, ...(detail.subscription || {}), invoices: detail.invoices || [], quotaUsage: detail.quotaUsage }} orgId={org.id} onPlanUpdated={() => { setDetailCache((prev) => { const next = new Map(prev); next.delete(org.id); return next; }); loadDetail(); }} />}
                    {activeTab === 'config' && <ClientConfigTab orgId={org.id} orgName={org.name} />}
                    {isPlatformSuperAdmin && activeTab === 'library' && <OrgLibraryTab orgId={org.id} orgName={org.name} />}
                  </div>
                </>
              ) : (
                <EmptyState text="Unable to load organisation details" />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ─── org detail page (routed by slug) ─────────────────────────────────── */

const TOKENS_PER_CREDIT = 1000;

export function InternalOrgDetail({ slug, onBack, setActiveView }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [usageRange, setUsageRange] = useState({ preset: '30d', from: null, to: null });
  const [orgStatusUpdating, setOrgStatusUpdating] = useState(false);

  // Auth context for super admin check
  const { isPlatformSuperAdmin } = useAuth();

  // Candidate insight modal state
  const [showCandidateInsightModal, setShowCandidateInsightModal] = useState(false);
  const [selectedCandidateInsight, setSelectedCandidateInsight] = useState(null);
  const [candidateInsightTab, setCandidateInsightTab] = useState('score');

  // Resume score click handler for super admins
  const handleResumeScoreClick = (candidate, tab = 'score') => {
    if (!isPlatformSuperAdmin) return;
    setSelectedCandidateInsight(candidate);
    setCandidateInsightTab(tab);
    setShowCandidateInsightModal(true);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = buildUsageRangeParams(usageRange);

    axios
      .get(`/api/internal/org-by-slug/${encodeURIComponent(slug)}`, { params })
      .then(({ data }) => {
        if (!cancelled) setDetail(data);
      })
      .catch((err) => {
        if (!cancelled)
          setError(err?.response?.data?.message || 'Failed to load organisation');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug, usageRange]);

  const org = useMemo(() => {
    if (!detail?.subscription) return null;
    const sub = detail.subscription;
    const llm = detail.llmUsage?.groups?.[0] || detail.llmUsage?.summary || {};
    const ai = detail.aiInterviewUsage || {};
    const totalCredits =
      typeof sub.total_ai_credits === 'number' ? sub.total_ai_credits : 0;
    // For routed org detail view, prefer server-derived values (based on AI interview sessions).
    const creditsUsed = detail.creditsUsed ?? (ai.sessionCount || 0);
    const creditsRemaining =
      detail.creditsRemaining ??
      (totalCredits ? Math.max(totalCredits - creditsUsed, 0) : 0);
    let usagePct =
      detail.usagePct ??
      (totalCredits > 0 ? Math.min((creditsUsed / totalCredits) * 100, 100) : 0);
    usagePct = Math.round(usagePct * 1000) / 1000;
    const supportOpen = (detail.supportRequests || []).filter(
      (r) => r.status === 'open' || r.status === 'in_progress'
    ).length;
    return {
      id: String(sub._id),
      name: sub.name,
      plan: sub.plan || 'free',
      status: sub.status || 'active',
      billingPeriod: sub.billingPeriod,
      totalAiCredits: totalCredits,
      planMonthlyPrice: sub.planMonthlyPrice,
      planCurrency: sub.planCurrency,
      createdAt: sub.createdAt,
      package_expiry_date: sub.package_expiry_date,
      company_logo_url: sub.company_logo_url,
      members: (detail.users || []).length,
      teams: detail.teams || [],
      support: { open: supportOpen, total: (detail.supportRequests || []).length },
      llm,
      ai,
      creditsUsed,
      creditsRemaining,
      usagePct,
      resumeUploadsTotal: detail.resumeUploadsTotal ?? 0,
      ocrCallsTotal: detail.ocrCallsTotal ?? 0,
    };
  }, [detail]);

  if (loading && !detail) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Spinner className="h-8 w-8" />
        <p className="mt-3 text-sm text-gray-500">Loading organisation…</p>
      </div>
    );
  }

  if (error || !org) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Dashboard
        </button>
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-600">
          {error || 'Organisation not found'}
        </div>
      </div>
    );
  }

  const reloadDetail = () => {
    setDetail(null);
    setLoading(true);
    axios
      .get(`/api/internal/org-by-slug/${encodeURIComponent(slug)}`)
      .then(({ data }) => setDetail(data))
      .catch((err) => setError(err?.response?.data?.message || 'Failed to reload'))
      .finally(() => setLoading(false));
  };

  const handleToggleOrgStatus = async () => {
    const deactivating = org.status !== 'inactive';
    const confirmMsg = deactivating
      ? `Deactivate "${org.name}"? Their users will be immediately signed out and unable to log in. No data will be deleted, and you can reactivate at any time.`
      : `Reactivate "${org.name}"? Their users will be able to log in again.`;
    if (!window.confirm(confirmMsg)) return;
    setOrgStatusUpdating(true);
    try {
      await axios.patch(
        `/api/internal/org/${org.id}/${deactivating ? 'deactivate' : 'reactivate'}`
      );
      reloadDetail();
    } catch (err) {
      window.alert(err?.response?.data?.message || 'Failed to update organisation status');
    } finally {
      setOrgStatusUpdating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Dashboard
        </button>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <div className="flex flex-wrap items-center gap-4 px-5 py-4 border-b border-gray-100">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-blue-500 shadow-sm">
            <Building2 className="h-5 w-5 text-white" />
          </div>
          <div className="flex flex-1 flex-wrap items-center gap-2 min-w-0">
            <h1 className="text-lg font-semibold text-gray-900">{org.name}</h1>
            <Badge className={planColor(org.plan)}>{capitalize(org.plan)}</Badge>
            <Badge className={statusColor(org.status)}>{capitalize(org.status)}</Badge>
          </div>
          <span className="text-xs text-gray-500">
            {org.members} members · {org.teams?.length ?? 0} teams · {org.support?.open ?? 0} open support
          </span>
          <button
            type="button"
            onClick={handleToggleOrgStatus}
            disabled={orgStatusUpdating}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-60',
              org.status === 'inactive'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                : 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
            )}
          >
            {orgStatusUpdating
              ? 'Updating…'
              : org.status === 'inactive'
              ? 'Reactivate Client'
              : 'Deactivate Client'}
          </button>
        </div>

        <div className="px-5 py-5 space-y-5">
          <TabPills 
            tabs={isPlatformSuperAdmin ? [...ORG_TABS, { key: 'library', label: 'Library' }] : ORG_TABS} 
            active={activeTab} 
            onChange={setActiveTab} 
          />
          <div className="mt-4">
            {activeTab === 'overview' && (
              <OverviewTab
                detail={{ ...org, ...(detail.subscription || {}), llm: org.llm, ai: org.ai, otherUsage: detail.otherUsage, quotaUsage: detail.quotaUsage, quotas: detail.quotaUsage }}
                usageRange={usageRange}
                onUsageRangeChange={setUsageRange}
              />
            )}
            {activeTab === 'teams' && <TeamsTab detail={detail} />}
            {activeTab === 'jobs' && <JobPostingsTab detail={detail} isPlatformSuperAdmin={isPlatformSuperAdmin} handleResumeScoreClick={handleResumeScoreClick} />}
            {activeTab === 'support' && <SupportTab detail={detail} setActiveView={setActiveView} />}
            {activeTab === 'billing' && (
              <BillingTab
                detail={{ ...org, ...(detail.subscription || {}), invoices: detail.invoices || [], quotaUsage: detail.quotaUsage }}
                orgId={org.id}
                onPlanUpdated={() => {
                  setDetail(null);
                  setLoading(true);
                  axios.get(`/api/internal/org-by-slug/${encodeURIComponent(slug)}`).then(({ data }) => setDetail(data)).catch(() => setError('Failed to reload')).finally(() => setLoading(false));
                }}
              />
            )}
            {activeTab === 'dpa' && (
              <DpaManagementTab
                orgId={org.id}
                orgName={detail?.subscription?.name || org.name}
                orgEmail={detail?.subscription?.email || ''}
              />
            )}
            {activeTab === 'config' && <ClientConfigTab orgId={org.id} orgName={org.name} />}
            {isPlatformSuperAdmin && activeTab === 'library' && <OrgLibraryTab orgId={org.id} orgName={org.name} />}
          </div>
        </div>
      </div>

      {/* Candidate Insight Modal for Super Admins */}
      {showCandidateInsightModal && selectedCandidateInsight && (
        <CandidateInsightModal
          isOpen={showCandidateInsightModal}
          candidate={selectedCandidateInsight}
          onClose={() => {
            setShowCandidateInsightModal(false);
            setSelectedCandidateInsight(null);
          }}
          initialTab={candidateInsightTab}
        />
      )}
    </div>
  );
}

/* ─── main dashboard ──────────────────────────────────────────────────── */

export default function InternalDashboard({
  dashboardStats,
  orgOverview,
  orgOverviewLoading,
  orgOverviewError,
  setActiveView,
  navigate: navigateProp,
}) {
  const navigateHook = useNavigate();
  const navigate = navigateProp ?? navigateHook;
  const stats = dashboardStats || {};
  const orgs = orgOverview || [];

  // Auth context for super admin check
  const { isPlatformSuperAdmin } = useAuth();

  // Candidate insight modal state
  const [showCandidateInsightModal, setShowCandidateInsightModal] = useState(false);
  const [selectedCandidateInsight, setSelectedCandidateInsight] = useState(null);
  const [candidateInsightTab, setCandidateInsightTab] = useState('score'); // Default to score tab for super admin

  // Extraction Fallbacks modal state
  const [showFallbacksModal, setShowFallbacksModal] = useState(false);

  // Resume score click handler for super admins
  const handleResumeScoreClick = (candidate, tab = 'score') => {
    if (!isPlatformSuperAdmin) return;
    setSelectedCandidateInsight(candidate);
    setCandidateInsightTab(tab);
    setShowCandidateInsightModal(true);
  };

  const monthlyRevenue = useMemo(
    () =>
      orgs
        .filter((o) => (o.plan || '').toLowerCase() !== 'free')
        .reduce((sum, o) => sum + (Number(o.planMonthlyPrice) || 0), 0),
    [orgs],
  );

  const billingPeriodSummary = useMemo(() => {
    const periods = [...new Set(orgs.map((o) => capitalize(o.billingPeriod)).filter(Boolean))];
    return periods.join(', ') || 'Monthly';
  }, [orgs]);

  const totalResumeUploads = useMemo(
    () => orgs.reduce((sum, o) => sum + (o.resumeUploadsTotal || 0), 0),
    [orgs]
  );

  const platformCostUsd = useMemo(
    () =>
      orgs.reduce(
        (sum, o) =>
          sum +
          (Number(o.llm?.estimatedCostUsd) || 0) +
          (Number(o.ai?.estimatedLivekitCostUsd) || 0),
        0,
      ),
    [orgs],
  );

  const PLATFORM_COST_USD_TO_INR = 92;
  const platformCostInr = platformCostUsd * PLATFORM_COST_USD_TO_INR;

  const summaryCards = [
    {
      icon: Building2,
      iconBg: 'bg-[#007BFF]',
      label: 'Total Organisations',
      value: stats.organisations?.total ?? '—',
      subtitle: `${stats.organisations?.active ?? 0} active`,
    },
    {
      icon: Users,
      iconBg: 'bg-indigo-500',
      label: 'Total Members',
      value: stats.orgUsers?.toLocaleString() ?? '—',
      subtitle: 'Across all organisations',
    },
    {
      icon: DollarSign,
      iconBg: 'bg-emerald-500',
      label: 'Monthly Revenue',
      value: fmtINR(monthlyRevenue),
      subtitle: billingPeriodSummary,
    },
    {
      icon: TrendingUp,
      iconBg: 'bg-violet-500',
      label: 'Platform Cost',
      value: fmtINRDec(platformCostInr),
      subtitle: 'LLM + LiveKit combined (est.)',
    },
    {
      icon: Upload,
      iconBg: 'bg-rose-500',
      label: 'Resume Uploads',
      value: totalResumeUploads.toLocaleString(),
      subtitle: 'Monotonic per screening',
    },
    {
      icon: AlertTriangle,
      iconBg: 'bg-orange-500',
      label: 'Extraction Fallbacks',
      value: `${stats.extractionFallbacks?.total ?? 0} of ${totalResumeUploads.toLocaleString()}`,
      subtitle: `${stats.extractionFallbacks?.failed ?? 0} failed · ${stats.extractionFallbacks?.recovered ?? 0} recovered`,
      onClick: () => setShowFallbacksModal(true),
    },
  ];

  return (
    <div className="space-y-10">
      {/* ── header ─────────────────────────────────────────────────── */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Internal Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Track client usage, billing, and platform health at a glance.
        </p>
      </div>

      {/* ── summary cards ──────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {summaryCards.map((c, i) => (
          <StatCard key={i} {...c} />
        ))}
      </div>

      <ExtractionFallbacksModal
        isOpen={showFallbacksModal}
        onClose={() => setShowFallbacksModal(false)}
      />

      {/* ── organisations section ──────────────────────────────────── */}
      <section>
        <div className="flex items-center gap-3 mb-5">
          <h2 className="text-lg font-semibold text-gray-900">Client Organisations</h2>
          <Badge className="bg-[#007BFF]/10 text-[#007BFF]">{orgs.length}</Badge>
        </div>

        {orgOverviewLoading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner className="h-6 w-6" />
          </div>
        ) : orgOverviewError ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-600">
            {orgOverviewError}
          </div>
        ) : orgs.length === 0 ? (
          <EmptyState text="No organisations found" />
        ) : (
          <div className="space-y-4">
            {orgs.map((org) => {
              const usagePct = org.usagePct || 0;
              const expiringDays = org.package_expiry_date
                ? daysBetween(new Date(), org.package_expiry_date)
                : Infinity;
              const isWarning = usagePct >= 80 || expiringDays <= 30;
              const routeSlug = org.slug || org.id;
              return (
                <button
                  key={org.id}
                  data-testid={`org-row-${org.id}`}
                  type="button"
                  onClick={() => navigate(`/internal/org/${routeSlug}`)}
                  className={cn(
                    'flex w-full items-center gap-4 rounded-2xl border px-5 py-4 text-left transition-colors hover:bg-white/80 hover:shadow-sm',
                    isWarning
                      ? 'border-rose-300 bg-rose-50/40'
                      : 'border-gray-200 bg-white',
                  )}
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-blue-500 shadow-sm">
                    <Building2 className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex flex-1 flex-wrap items-center gap-2 min-w-0">
                    <span className="text-sm font-semibold text-gray-900 truncate">{org.name}</span>
                    <Badge className={planColor(org.plan)}>{capitalize(org.plan)}</Badge>
                    <Badge className={statusColor(org.status)}>{capitalize(org.status)}</Badge>
                    <DpaStatusPill acceptedAt={org.dpaAcceptedAt} />
                  </div>
                  <span className="hidden md:inline text-xs text-gray-400 shrink-0 tabular-nums">
                    {org.members ?? 0} members · {org.teams ?? 0} teams · {org.support?.open ?? 0} open support
                  </span>
                  <div className="hidden sm:flex items-center gap-2 shrink-0 w-40">
                    {usagePct >= 80 ? (
                      <>
                        <span className="text-xs font-medium text-red-600 tabular-nums">{fmtPct3(usagePct)}% credits used</span>
                        <div className="flex-1">
                          <ProgressBar pct={usagePct} colorClass="bg-red-500" />
                        </div>
                      </>
                    ) : expiringDays <= 30 ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        Expires in {expiringDays}d
                      </span>
                    ) : (
                      <>
                        <span className="text-xs text-gray-400 tabular-nums">{fmtPct3(usagePct)}%</span>
                        <div className="flex-1">
                          <ProgressBar pct={usagePct} colorClass="bg-indigo-500" />
                        </div>
                      </>
                    )}
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* Candidate Insight Modal for Super Admins */}
      {showCandidateInsightModal && selectedCandidateInsight && (
        <CandidateInsightModal
          isOpen={showCandidateInsightModal}
          candidate={selectedCandidateInsight}
          onClose={() => {
            setShowCandidateInsightModal(false);
            setSelectedCandidateInsight(null);
          }}
          initialTab={candidateInsightTab}
        />
      )}
    </div>
  );
}
